import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePageTraffic } from "@/lib/usePageTraffic";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, ScanLine, FileText, Layers, Zap, Clock, Sparkles,
  AlertTriangle, CheckCircle2, TrendingUp, ChevronRight, Loader2, ClipboardList,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, describeError } from "@/lib/api";
import { toast } from "sonner";
import {
  listEobFiles, runEraValidation, acknowledgeReconciliation,
  linkPayer, linkPayee, linkPayeeNpi, runDeepRecovery, getEra, getMapperGaps, getEobQueueHealth,
} from "@/lib/eobEraV2";
import { getNoteQueueHealth } from "@/lib/noteClaim";
import { listSplitBatches } from "@/lib/patientDocs";
import { useClient } from "@/lib/clientContext";
import { Link2, RotateCw, Wand2, Download } from "lucide-react";
import CheckWorkspace from "@/components/eob/CheckWorkspace";
import InsuranceModal from "@/components/claim/InsuranceModal";
import ClaimWorkspace from "@/components/claim/ClaimWorkspace";
import NeedsYouBrief from "@/components/home/NeedsYouBrief";
import { BriefSkeleton, SoftValue } from "@/components/Skeletons";
import {
  readiness, estCharge, money, kMoney, STATE_PILL, patientOf, payerOf, cptsOf, sourceOf, posOf, BLOCKERS,
} from "@/lib/claimReadiness";

const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
};
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

// EOB-to-ERA execution helpers (files endpoint exposes status + completed_at only).
const parseSummary = (f) => {
  const s = f?.summary || f?.extraction_summary;
  if (!s) return {};
  if (typeof s === "string") { try { return JSON.parse(s); } catch { return {}; } }
  return s;
};
const cleanEobErr = (e) => {
  if (!e) return "Extraction failed";
  const s = String(e);
  if (/token limit/i.test(s)) return "Extraction limit reached — retry later";
  const m = s.match(/['"]message['"]:\s*["']([^"']+)["']/);
  if (m) return m[1].slice(0, 70);
  return s.replace(/^\d+:\s*/, "").slice(0, 70);
};
// Three real buckets from the files endpoint (status + completed_at only).
const eobBucket = (f) => {
  const st = (f.status || "").toLowerCase();
  if (st === "failed" || st === "error") return "failed";
  if (st === "processed" || st === "complete") return "complete";
  return "inprogress"; // processing / queued / pending
};
const fmtEobTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefCount, setBriefCount] = useState(null);

  const { selectedClientId } = useClient();
  const { report: reportTraffic } = usePageTraffic("home");
  const homeFirst = useRef(true);
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!selectedClientId) { setNotes([]); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/api/edi/note-to-claim/notes", { params: { client_id: selectedClientId, limit: 2000 }, etag: true });
      const list = Array.isArray(data?.notes) ? data.notes : [];
      list.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setNotes(list);
      if (!silent && homeFirst.current) { homeFirst.current = false; reportTraffic(list.length); }
    } catch { if (!silent) setNotes([]); } finally { if (!silent) setLoading(false); }
  }, [selectedClientId, reportTraffic]);
  // First mount shows the skeleton; every later refresh refetches in the background
  // so linking insurance / editing a claim never flashes the whole page.
  const reloadNotes = useCallback(() => load({ silent: true }), [load]);
  useEffect(() => { load(); }, [load]);

  const enriched = useMemo(() => notes.map((n) => ({ n, r: readiness(n) })), [notes]);

  const stats = useMemo(() => {
    const c = { ready: 0, needs_review: 0, needs_coding: 0 };
    const d = { ready: 0, needs_review: 0, needs_coding: 0 };
    let totalEst = 0, msSum = 0, msN = 0;
    enriched.forEach(({ n, r }) => {
      c[r.bucket] += 1; const e = estCharge(n); d[r.bucket] += e; totalEst += e;
      const ms = n.agent_metrics?.total_ms; if (ms) { msSum += ms; msN += 1; }
    });
    const total = enriched.length;
    return {
      c, d, total, totalEst,
      todayCount: enriched.filter(({ n }) => isToday(n.created_at)).length,
      readyPct: total ? Math.round((c.ready / total) * 100) : 0,
      avgSec: msN ? (msSum / msN / 1000).toFixed(1) : null,
    };
  }, [enriched]);

  // Real EOB-to-ERA execution feed (same upstream the "+ New Claim → EOB to ERA" flow uses).
  const [eobFiles, setEobFiles] = useState([]);
  const [eobLoading, setEobLoading] = useState(true);
  const loadEobs = useCallback(async () => {
    try {
      const files = await listEobFiles({ since: "all", limit: 200 });
      files.sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")));
      setEobFiles(files);
    } catch { /* keep prior list on transient error */ } finally { setEobLoading(false); }
  }, []);
  useEffect(() => { loadEobs(); }, [loadEobs]);

  const anyEobProcessing = useMemo(
    () => eobFiles.some((f) => f.status === "processing" || f.status === "queued" || (f.status === "processed" && f.audit_status !== "complete")),
    [eobFiles],
  );
  // Poll for live status only while something is still processing.
  useEffect(() => {
    if (!anyEobProcessing) return undefined;
    const t = setInterval(loadEobs, 4000);
    return () => clearInterval(t);
  }, [anyEobProcessing, loadEobs]);

  // Notes: refresh the moment a background/watch job is registered elsewhere (AppShell),
  // and poll quickly while any note is still being coded so Today's Uploads tracks it live.
  const anyNoteProcessing = useMemo(() => notes.some((n) => {
    const st = (n.status || "").toLowerCase(); const stg = (n.stage || "").toLowerCase();
    return (stg && stg !== "done") || st === "queued" || st === "processing" || st === "pending";
  }), [notes]);
  const [pendingJobs, setPendingJobs] = useState([]);
  useEffect(() => {
    const onJobs = (e) => {
      const d = e?.detail;
      if (d?.id) {
        if (d.done) setPendingJobs((prev) => prev.filter((j) => j.id !== d.id));
        else setPendingJobs((prev) => (prev.some((j) => j.id === d.id) ? prev : [...prev, { id: d.id, filename: d.filename }]));
      }
      reloadNotes();
    };
    window.addEventListener("revq:jobs-changed", onJobs);
    return () => window.removeEventListener("revq:jobs-changed", onJobs);
  }, [reloadNotes]);
  // Drop a pending job once its real note lands as done (belt-and-suspenders vs. the completion event).
  useEffect(() => {
    if (!pendingJobs.length) return;
    const doneIds = new Set(notes.filter((n) => {
      const s = (n.status || "").toLowerCase(); const st = (n.stage || "").toLowerCase();
      return s === "ready" || s === "failed" || st === "done";
    }).map((n) => n.id));
    setPendingJobs((prev) => { const next = prev.filter((j) => !doneIds.has(j.id)); return next.length === prev.length ? prev : next; });
  }, [notes, pendingJobs.length]);
  useEffect(() => {
    if (!anyNoteProcessing) return undefined;
    const t = setInterval(reloadNotes, 1500);
    return () => clearInterval(t);
  }, [anyNoteProcessing, reloadNotes]);

  // Lens document uploads — scoped to the client in context.
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    if (!selectedClientId) { setDocs([]); return; }
    api.get("/api/edi/documents/analyses", { params: { client_id: selectedClientId, limit: 100, view: "summary" } })
      .then((r) => setDocs(Array.isArray(r.data?.analyses) ? r.data.analyses : []))
      .catch(() => setDocs([]));
  }, [selectedClientId]);

  const uploadToday = useMemo(() => {
    const items = buildUploads(notes, eobFiles, docs, true);
    const c = { total: items.length, processing: 0, ready: 0, failed: 0 };
    items.forEach((u) => { c[u.progress] += 1; });
    return c;
  }, [notes, eobFiles, docs]);

  const dateStr = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  return (
    <div data-testid="dashboard-root" className="flex flex-col gap-5 fade-up w-full max-w-[1400px] mx-auto px-6 py-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[24px] font-bold tracking-[-0.7px] text-[var(--ink)]" data-testid="home-greeting">
            {`${greeting()}${user?.first_name ? `, ${user.first_name}` : ""}`}
          </div>
          <div className="text-[13px] mt-1" style={{ color: "#6E6E6B" }} data-testid="home-subhead">
            {dateStr} · revQ coded <span style={{ color: "#6C6C69", fontWeight: 600 }}>{stats.total}</span> claim{stats.total === 1 ? "" : "s"} overnight{briefCount != null ? <> · <span style={{ color: "#6C6C69", fontWeight: 600 }}>{briefCount}</span> finding{briefCount === 1 ? "" : "s"} need{briefCount === 1 ? "s" : ""} you</> : null}
          </div>
        </div>
      </div>

      {loading ? (
        <HomeSkeleton />
      ) : (
        <TodayTab
          enriched={enriched} navigate={navigate} onCount={setBriefCount}
          notes={notes} eobFiles={eobFiles} docs={docs} notesReload={reloadNotes} eobsReload={loadEobs} pendingJobs={pendingJobs}
        />
      )}
    </div>
  );
}

function Sk({ className = "", style }) {
  return <div className={`shimmer rounded-[8px] ${className}`} style={style} />;
}

function HomeSkeleton() {
  return (
    <div className="flex flex-col gap-5" data-testid="home-skeleton" aria-busy="true">
      {/* needs-you brief — mirrors the real three-column layout so the first paint doesn't shift */}
      <BriefSkeleton />

      {/* today's uploads panel */}
      <div className="glass-surface rounded-[20px] p-5" style={{ minHeight: 220 }}>
        <div className="flex items-center justify-between mb-4">
          <Sk className="h-[15px] w-[160px]" />
          <Sk className="h-[12px] w-[120px] rounded-full" />
        </div>
        {[0, 1, 2].map((row) => (
          <div key={row} className="flex items-center gap-3 py-3 border-t border-black/[.06] first:border-t-0">
            <Sk className="h-[34px] w-[34px] rounded-[10px] flex-shrink-0" />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <Sk className="h-[13px]" style={{ width: `${52 - row * 6}%` }} />
              <Sk className="h-[10px] rounded-full" style={{ width: `${72 - row * 8}%` }} />
            </div>
            <Sk className="h-[16px] w-[52px] flex-shrink-0" />
          </div>
        ))}
      </div>

      {/* agent activity card — reserved height matches the loaded card */}
      <div className="glass-surface rounded-[20px] p-5" style={{ minHeight: 430 }}>
        <Sk className="h-[15px] w-[140px]" />
        <div className="flex gap-8 mt-5">
          <Sk className="h-[40px] w-[60px]" />
          <Sk className="h-[40px] w-[120px]" />
          <Sk className="h-[40px] w-[120px]" />
        </div>
        <div className="flex flex-col gap-3 mt-6">
          {[0, 1].map((i) => <Sk key={i} className="h-[40px] w-full rounded-[12px]" />)}
        </div>
      </div>
    </div>
  );
}

// ---- Today's uploads: merge note-to-claim + EOB/ERA into one live feed ----
const noteType = (n) => {
  const dt = ((n.documents || []).find((d) => d.doc_type) || {}).doc_type || "";
  if (/superbill/i.test(dt)) return { key: "sb", label: "Superbill" };
  return { key: "note", label: "Encounter note" };
};
const noteProgress = (n) => {
  const st = (n.status || "").toLowerCase(), stg = (n.stage || "").toLowerCase();
  if (st === "failed" || st === "error") return "failed";
  if (stg && stg !== "done") return "processing";
  if (st === "queued" || st === "processing") return "processing";
  return "ready";
};
const eobProgress = (f) => ({ inprogress: "processing", failed: "failed", complete: "ready" }[eobBucket(f)]);
const docTypeInfo = (d) => {
  const t = String(d.document_type || d.classification || "").toLowerCase();
  if (/insurance|card/.test(t)) return { key: "card", label: "Insurance card" };
  if (/gov|license|passport|\bid\b/.test(t)) return { key: "id", label: "Government ID" };
  if (/lab/.test(t)) return { key: "labs", label: "Labs" };
  if (/superbill/.test(t)) return { key: "sb", label: "Superbill" };
  if (/note|encounter|soap/.test(t)) return { key: "note", label: "Encounter note" };
  return { key: "doc", label: d.document_type || "Document" };
};
const docProgress = (d) => {
  const s = String(d.status || "").toLowerCase();
  if (/fail|error/.test(s)) return "failed";
  if (/complete|done|ready|processed/.test(s)) return "ready";
  return "processing";
};

function buildUploads(notes, eobFiles, docs, todayOnly = true) {
  const out = [];
  (notes || []).forEach((n) => {
    if (todayOnly && !isToday(n.created_at)) return;
    const t = noteType(n);
    const nm = patientOf(n);
    const named = nm && nm !== "Unnamed patient";
    const fileName = ((n.documents || []).find((d) => d.filename) || {}).filename || null;
    out.push({
      id: n.id, kind: "note", typeKey: t.key, typeLabel: t.label,
      title: named ? nm : "Name not read", nameRead: !!named,
      subtitle: named ? `${sourceOf(n)}${n.cpt_count ? ` \u00b7 ${n.cpt_count} CPT` : ""}` : `${t.label} \u00b7 ${sourceOf(n)}${fileName ? ` \u00b7 ${fileName}` : ""}`,
      fileName, cptCount: n.cpt_count || (n.cpt_codes || []).length, icdCount: n.icd_count || (n.icd_codes || []).length,
      provider: n.provider_name || null,
      progress: noteProgress(n), dest: "Claims Queue", time: n.created_at,
      stage: n.stage && n.stage !== "done" ? n.stage : null, raw: n,
    });
  });
  (eobFiles || []).forEach((f) => {
    if (todayOnly && !isToday(f.created_at)) return;
    const s = parseSummary(f);
    const amt = s.check_amount ?? s.total_paid ?? null;
    out.push({
      id: f.file_id, kind: "eob", typeKey: "eob", typeLabel: "EOB / ERA",
      title: s.payer_name || "Payer not read", nameRead: !!s.payer_name,
      checkNo: s.check_number || null, amount: amt, claims: s.claim_count ?? null,
      subtitle: [amt != null ? money(amt) : null, s.claim_count != null ? `${s.claim_count} claims matched` : null].filter(Boolean).join(" \u00b7 ") || "835 remittance",
      fileName: f.filename || null,
      progress: eobProgress(f), dest: "Payment Posting", time: f.created_at,
      stage: null, raw: f, error: f.error,
    });
  });
  (docs || []).forEach((d) => {
    if (todayOnly && !isToday(d.created_at)) return;
    const t = docTypeInfo(d);
    const extracted = d.extracted_name || d.patient_name || d.subject_name || null;
    const isId = t.key === "id" || t.key === "card";
    out.push({
      id: d.id || d.document_id, kind: "doc", typeKey: t.key, typeLabel: t.label,
      title: extracted || d.filename || t.label, nameRead: !!(extracted || d.filename),
      subtitle: [t.label, d.upload_method || d.origin].filter(Boolean).join(" \u00b7 "),
      extracted, fileName: d.filename || null,
      progress: docProgress(d), dest: isId ? "Patient record" : "Telemedicine", time: d.created_at,
      stage: null, raw: d,
    });
  });
  out.sort((a, b) => String(b.time || "").localeCompare(String(a.time || "")));
  return out;
}

function SortViewMenu({ groupVal, onGroup, sortVal, onSort, groupOpts, sortOpts, extra, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid={testid}
        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] bg-black/[.04] rounded-[9px] px-2.5 py-1.5 transition">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M1 2h10M2.7 6h6.6M4.6 10h2.8" /></svg>
        Sort · View
      </button>
      {open ? (
        <div className="absolute right-0 top-[112%] z-30 w-[230px] bg-white rounded-[13px] border border-black/[.1] shadow-[0_20px_50px_rgba(0,0,0,.18)] p-2" onMouseLeave={() => setOpen(false)}>
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--gray2)] px-2 py-1">Group by</div>
          {groupOpts.map((o) => (
            <button key={o.v} onClick={() => onGroup(o.v)} data-testid={`${testid}-grp-${o.v}`}
              className="w-full text-left px-2 py-1.5 rounded-[8px] hover:bg-black/[.05] text-[12px] flex items-center justify-between">
              {o.label}{groupVal === o.v ? <span className="text-[var(--indigo,#5856D6)]">✓</span> : null}
            </button>
          ))}
          <hr className="my-1.5 border-black/[.06]" />
          <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--gray2)] px-2 py-1">Sort within group</div>
          {sortOpts.map((o) => (
            <button key={o.v} onClick={() => onSort(o.v)} data-testid={`${testid}-sort-${o.v}`}
              className="w-full text-left px-2 py-1.5 rounded-[8px] hover:bg-black/[.05] text-[12px] flex items-center justify-between">
              {o.label}{sortVal === o.v ? <span className="text-[var(--indigo,#5856D6)]">✓</span> : null}
            </button>
          ))}
          {extra ? (
            <>
              <hr className="my-1.5 border-black/[.06]" />
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--gray2)] px-2 py-1">{extra.title}</div>
              {extra.opts.map((o) => (
                <button key={o.v} onClick={() => extra.onVal(o.v)} data-testid={`${testid}-cap-${o.v}`}
                  className="w-full text-left px-2 py-1.5 rounded-[8px] hover:bg-black/[.05] text-[12px] flex items-center justify-between">
                  {o.label}{extra.val === o.v ? <span className="text-[var(--indigo,#5856D6)]">✓</span> : null}
                </button>
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---- Needs you today: banded by blocker cluster ----
const AGING_DAYS = 90;
const NY_CLUSTERS = [
  { key: "recode", label: "Needs re-coding", tone: "blk", batch: "Open" },
  { key: "no_payer", label: "No payer on file", tone: "blk", batch: "Fix all" },
  { key: "no_dos", label: "Missing date of service", tone: "blk", batch: "Open" },
  { key: "code", label: "Code conflict", tone: "blk", batch: "Open" },
  { key: "review", label: "Coding review — can be submitted", tone: "wrn", batch: "Review" },
];
const BLOCKED_BANDS = new Set(["recode", "no_payer", "no_dos", "code"]);
const titleCase = (s) => String(s || "").toLowerCase().replace(/\b([a-z])/g, (m) => m.toUpperCase());
const normName = (n) => String(patientOf(n) || "").trim().toLowerCase().replace(/\s+/g, " ");
const claimAgeDays = (dos) => { if (!dos) return null; const d = (Date.now() - new Date(dos).getTime()) / 86400000; return Math.max(0, Math.round(d)); };
const isAging = (n) => { const a = claimAgeDays(n.date_of_service); return a != null && a >= AGING_DAYS; };

const cptCount = (n) => n.claim_readiness?.cpt_count ?? n.cpt_count ?? (n.cpt_codes || []).length;
const icdCount = (n) => n.claim_readiness?.icd_count ?? n.icd_count ?? (n.icd_codes || []).length;
const needsRecode = (n) => { const c = cptCount(n), i = icdCount(n); return (c > 0 && i === 0) || (c === 0 && i === 0); };
// All active blockers on a row (for multi-blocker secondary indicator).
const blockersOf = (n, r) => {
  const s = new Set();
  const cler = n.blockers_info?.clerical || [];
  if (r.blockers.includes("no_payer") || cler.some((x) => /insurance/i.test(x))) s.add("no_payer");
  if (r.blockers.includes("no_dos") || n.claim_readiness?.missing_dos || cler.some((x) => /dos/i.test(x))) s.add("no_dos");
  if (needsRecode(n)) s.add("recode");
  if (r.blockers.includes("unlinked") || r.blockers.includes("no_icd") || r.blockers.includes("no_cpt") || (n.claim_readiness?.blockers || 0) > 0) s.add("code");
  return s;
};
const clusterOf = (n, r) => {
  if (needsRecode(n)) return "recode"; // top priority: no codes to point at
  const b = blockersOf(n, r);
  if (b.has("no_payer")) return "no_payer";
  if (b.has("no_dos")) return "no_dos";
  if (b.has("code")) return "code";
  if ((n.claim_readiness?.needs_review || 0) > 0 || n.claim_readiness?.status === "warnings" || r.bucket === "needs_review") return "review";
  return null;
};
// Severity union: clerical + code + EDI blockers → cannot submit (NEEDS REVIEW).
const cannotSubmit = (n, r) => {
  const bi = n.blockers_info || {};
  return (bi.clerical || []).length > 0 || (bi.edi || []).length > 0 || (n.claim_readiness?.blockers || 0) > 0 || r.blockers.length > 0;
};
// A code is "coder-flagged" only when revQ genuinely wants a human to look.
// NOT flagged: in_corpus:false (informational — 83% of codes), "ok"/"green"/"unknown" statuses.
const BAD_FLAG = new Set(["rare", "red", "yellow", "fail", "warn", "warning", "high", "alert", "error"]);
const badStatus = (v) => BAD_FLAG.has(String(v).toLowerCase());
const icdFlagged = (c) =>
  (!!c?.provenance && c.provenance !== "clear") ||
  badStatus(c?.validation?.age) || badStatus(c?.validation?.gender);
const cptFlagged = (c) => Object.values(c?.flags || {}).some(badStatus);
const gatherSuggestions = (n) => {
  const out = [];
  (n.cpt_codes || []).forEach((c) => (c.suggestions || []).forEach((s) => {
    if (typeof s === "string") { out.push({ code: String(c.code), title: "Suggestion", why: s }); return; }
    out.push({ code: String(s.code || c.code || ""), title: String(s.title || s.label || s.type || "Suggestion"), why: String(s.detail || s.message || s.reason || s.description || s.rationale || "") });
  }));
  (n.icd_codes || []).forEach((c) => { if (icdFlagged(c)) out.push({ code: String(c.code), title: c.provenance && c.provenance !== "clear" ? "Provenance: " + c.provenance : "Age / gender check", why: String(c.rationale || c.description || "Verify this diagnosis is supported by the note.") }); });
  return out;
};

const KV = ({ k, v, tone }) => (
  <div className="flex items-center justify-between gap-2 py-[3px]">
    <span className="text-[10.5px] text-[#9B9B97] font-medium truncate min-w-0">{k}</span>
    <span className={`text-[11.5px] font-semibold whitespace-nowrap flex-shrink-0 ${tone === "r" ? "text-[#C4302A]" : tone === "a" ? "text-[#9A5A00]" : "text-[var(--ink)]"}`}>{v}</span>
  </div>
);
// ---- CMS-1500 view model built from real note data (cpt.dx[] carries the pointers) ----
const LETTERS = "ABCDEFGHIJKL";
function claimView(n) {
  const dx = (n.icd_codes || []).map((c, i) => ({
    code: String(c.code || ""), letter: LETTERS[i] || "·",
    primary: i === 0, flag: icdFlagged(c),
    why: c.rationale || c.description || "",
  }));
  const letterOf = (code) => (dx.find((d) => d.code === code) || {}).letter;
  const lines = (n.cpt_codes || []).map((c) => {
    const ptrs = (c.dx || []).map((d) => letterOf(d.code)).filter(Boolean);
    const mods = c.billing_modifiers || c.modifiers || [];
    return {
      code: String(c.code || ""), mods: (Array.isArray(mods) ? mods : []).filter(Boolean),
      ptrs, noPtr: !(c.dx || []).length, flag: cptFlagged(c),
    };
  });
  const flagged = dx.filter((d) => d.flag).length + lines.filter((l) => l.flag).length;
  const noPtr = lines.filter((l) => l.noPtr);
  return { dx, lines, flagged, noPtr };
}
function claimWhy(v) {
  if (!v.lines.length && !v.dx.length) return "The note produced no codes at all. Adding a date will not make it billable — it needs re-coding.";
  if (!v.lines.length) return "The note produced a diagnosis but no billable procedure. revQ found no CPT it could defend against this provider's billed lines.";
  if (v.noPtr.length) return `${v.noPtr[0].code} has no pointer to a diagnosis. Until every line points to a Dx, this claim cannot be saved — any version already saved is unaffected.`;
  if (v.flagged) { const f = v.dx.find((d) => d.flag); return (f && f.why) || "A code was flagged for review — verify it is supported by the note."; }
  return "All codes matched the provider's billed-line history. No flags raised.";
}
function claimLabel(v) {
  if (!v.lines.length && !v.dx.length) return { text: "nothing coded from this note", empty: true };
  if (v.noPtr.length) return { text: `${v.noPtr.length} line${v.noPtr.length > 1 ? "s" : ""} with no diagnosis pointer`, bad: true };
  if (v.flagged) return { text: `${v.flagged} flagged for review` };
  return { text: "pointers set · no edits" };
}
const CXV = { base: { background: "#F1F1F4", color: "#6C6C69" }, flag: { background: "#FDF4E5", color: "#9A5A00" }, blocked: { background: "#FDEDEC", color: "#C4302A" } };
const Chip = ({ variant = "base", children }) => (
  <span className="inline-flex items-center rounded-[6px] px-[7px] py-[3px] text-[10.5px] font-semibold flex-shrink-0" style={{ ...CXV[variant], fontFamily: "ui-monospace,SFMono-Regular,Menlo,monospace" }}>{children}{variant === "flag" ? <b className="font-bold ml-0.5">!</b> : null}</span>
);
const Ltr = ({ children }) => <b className="font-sans font-bold text-[9.5px] text-[#9B9B97] mr-0.5">{children}</b>;
const Ptr = ({ children }) => <b className="font-sans font-semibold text-[9.5px] rounded-[4px] px-1 ml-1" style={{ background: "rgba(20,20,18,.07)" }}>{children}</b>;
const NoPtrBadge = () => <b className="font-sans font-semibold text-[9.5px] rounded-[4px] px-1 ml-1 text-white" style={{ background: "#FF3B30" }}>no Dx</b>;
function ClaimDetail({ v }) {
  return (
    <div className="p-3.5 text-left">
      <h5 className="text-[9.5px] tracking-[.06em] uppercase text-[#9B9B97] font-semibold mb-2">Service lines · Box 24</h5>
      {v.lines.length ? v.lines.map((l, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <Chip variant={l.noPtr ? "blocked" : l.flag ? "flag" : "base"}>{l.code}{l.mods.map((m) => `-${m}`).join("")}</Chip>
          <span className="text-[10.5px] text-[#6E6E6B]">{l.noPtr ? "no diagnosis pointer — blocks saving" : l.ptrs.length ? `points to ${l.ptrs.join(", ")}` : "—"}</span>
        </div>
      )) : <span className="text-[10.5px] text-[#9B9B97]">no service lines</span>}
      <h5 className="text-[9.5px] tracking-[.06em] uppercase text-[#9B9B97] font-semibold mb-2 mt-2.5">Diagnoses · Box 21</h5>
      {v.dx.length ? v.dx.map((d, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <Chip variant={d.flag ? "flag" : "base"}><Ltr>{d.letter}</Ltr>{d.code}</Chip>
          <span className="text-[10.5px] text-[#6E6E6B]">{d.primary ? "primary" : "secondary"}{d.flag ? " · flagged" : ""}</span>
        </div>
      )) : <span className="text-[10.5px] text-[#9B9B97]">no diagnoses</span>}
      <div className="text-[10.5px] text-[#6E6E6B] leading-[1.55] border-t border-[#F1F1F4] mt-2 pt-2"><b className="text-[var(--ink)]">Why it is flagged.</b> {claimWhy(v)}</div>
    </div>
  );
}
// Inline CPT/Dx chip strip (≥1560px), boxed like the mock; overflow chips collapse into a +N tail.
function ChipStrip({ v }) {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    const el = ref.current; if (!el) return undefined;
    const fit = () => {
      const chips = Array.from(el.querySelectorAll("[data-chip]"));
      const tail = el.querySelector("[data-tail]");
      chips.forEach((c) => { c.style.display = ""; });
      if (tail) tail.style.display = "none";
      if (el.scrollWidth <= el.clientWidth) return;
      if (tail) tail.style.display = "";
      let hidden = 0;
      for (let i = chips.length - 1; i >= 0; i--) { chips[i].style.display = "none"; hidden += 1; if (el.scrollWidth <= el.clientWidth) break; }
      if (tail) tail.textContent = `+${hidden}`;
    };
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, [v]);
  const lab = claimLabel(v);
  return (
    <div className="hidden min-[1560px]:flex flex-col justify-center gap-1 rounded-[9px] px-[11px] py-[7px] relative group" style={{ flex: 2, minWidth: 0, background: "#FCFCFD", border: "1px solid #F1F1F4" }} tabIndex={0} data-testid="ny-chipstrip">
      <span className={`text-[10.5px] whitespace-nowrap overflow-hidden text-ellipsis ${lab.bad ? "font-semibold" : ""}`} style={{ color: lab.bad ? "#C4302A" : "#9B9B97" }}>{lab.text}</span>
      {lab.empty ? null : (
        <div ref={ref} className="flex items-center gap-1 overflow-hidden" style={{ height: 24 }}>
          <span className="text-[9.5px] tracking-[.06em] font-bold text-[#9B9B97] mr-0.5 flex-shrink-0">CPT</span>
          {v.lines.length ? v.lines.map((l, i) => (
            <span data-chip key={"c" + i} className="flex-shrink-0"><Chip variant={l.noPtr ? "blocked" : l.flag ? "flag" : "base"}>{l.code}{l.mods.map((m) => `-${m}`).join("")}{l.noPtr ? <NoPtrBadge /> : l.ptrs.length ? <Ptr>{l.ptrs.join(",")}</Ptr> : null}</Chip></span>
          )) : <span className="text-[10.5px] text-[#9B9B97] flex-shrink-0">none</span>}
          <span className="w-px h-4 mx-1 flex-shrink-0" style={{ background: "#E9E9EC" }} />
          <span className="text-[9.5px] tracking-[.06em] font-bold text-[#9B9B97] mr-0.5 flex-shrink-0">Dx</span>
          {v.dx.length ? v.dx.map((d, i) => (
            <span data-chip key={"d" + i} className="flex-shrink-0"><Chip variant={d.flag ? "flag" : "base"}><Ltr>{d.letter}</Ltr>{d.code}</Chip></span>
          )) : <span className="text-[10.5px] text-[#9B9B97] flex-shrink-0">none</span>}
          <span data-tail className="text-[10.5px] font-semibold text-[#6C6C69] border border-[#E9E9EC] rounded-[6px] px-[7px] py-[2px] flex-shrink-0" style={{ display: "none" }}>+0</span>
        </div>
      )}
      <div className="absolute left-0 top-[calc(100%+6px)] z-30 w-[330px] bg-white rounded-[11px] border border-[#E9E9EC] shadow-[0_10px_32px_rgba(20,20,18,.15)] opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"><ClaimDetail v={v} /></div>
    </div>
  );
}


// Collapse a band's notes into patient rows (join on name until MRN returns).
function collapsePatients(items) {
  const m = {};
  items.forEach(({ n, r }) => { const k = normName(n); (m[k] ||= []).push({ n, r }); });
  return Object.values(m).map((rows) => {
    const notes = rows.map((x) => x.n);
    const dates = notes.map((x) => x.date_of_service).filter(Boolean).sort();
    return {
      key: notes[0].id, rep: rows[0], notes, count: notes.length,
      name: titleCase(patientOf(notes[0])), earliestDos: dates[0] || null,
      est: notes.reduce((s, x) => s + estCharge(x), 0),
    };
  });
}

function InsuranceStrip({ notes, count, onReload, onClose }) {
  const [payers, setPayers] = React.useState([]);
  const [payerId, setPayerId] = React.useState("");
  const [memberId, setMemberId] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [err, setErr] = React.useState("");
  const fileRef = React.useRef(null);
  const cid = notes[0]?.client_id;
  React.useEffect(() => {
    api.get("/api/edi/note-to-claim/payers", { params: cid ? { client_id: cid } : {} })
      .then((r) => setPayers(r.data?.payers || [])).catch(() => setPayers([]));
  }, [cid]);
  const b64 = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1]); r.onerror = rej; r.readAsDataURL(f); });
  const onScan = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy("scan"); setErr("");
    try {
      const data = (await api.post(`/api/edi/note-to-claim/notes/${notes[0].id}/scan-insurance-card`, { file_base64: await b64(f) })).data;
      const ins = data?.insurance || data || {};
      if (ins.payer_id) setPayerId(ins.payer_id);
      if (ins.member_id) setMemberId(ins.member_id);
    } catch (ex) { setErr(describeError(ex)); } finally { setBusy(""); }
  };
  const onLink = async () => {
    if (!payerId) { setErr("Choose an approved payer."); return; }
    setBusy("link"); setErr("");
    const p = payers.find((x) => String(x.id) === String(payerId));
    try {
      for (const n of notes) await api.patch(`/api/edi/note-to-claim/notes/${n.id}/insurance`, { payer_id: payerId, payer_name: p?.name, member_id: memberId || undefined });
      toast.success(`Insurance linked${count > 1 ? ` · applied to all ${count} claims` : ""}`);
      await onReload?.(); onClose?.();
    } catch (ex) { setErr(describeError(ex)); } finally { setBusy(""); }
  };
  const filled = payerId && memberId;
  return (
    <div className="bg-black/[.03] rounded-[12px] p-3.5 mb-2" data-testid="ny-ins-strip">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[11px] font-bold text-[var(--ink)]">Link insurance{count > 1 ? ` · applies to all ${count} claims` : ""}</div>
        <span className="flex-1" />
        <button onClick={onClose} className="text-[11px] font-semibold text-[var(--gray2)]">Close</button>
      </div>
      <div className="flex items-end gap-2 mt-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] font-semibold text-[var(--gray3)] uppercase">Payer</label>
          <select value={payerId} onChange={(e) => setPayerId(e.target.value)} data-testid="ny-ins-payer" className="w-full mt-1 px-3 py-2 rounded-[9px] border border-black/[.12] text-[12.5px] bg-white">
            <option value="">Select approved payer…</option>
            {payers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.id}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-[10px] font-semibold text-[var(--gray3)] uppercase">Member ID</label>
          <input value={memberId} onChange={(e) => setMemberId(e.target.value)} placeholder="Member / subscriber ID" data-testid="ny-ins-member" className="w-full mt-1 px-3 py-2 rounded-[9px] border border-black/[.12] text-[12.5px] bg-white" />
        </div>
        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onScan} data-testid="ny-ins-file" />
        <button onClick={filled ? onLink : () => fileRef.current?.click()} disabled={!!busy} data-testid="ny-ins-action"
          className="text-[12px] font-bold text-white bg-[var(--ink)] rounded-[10px] px-4 py-2 inline-flex items-center gap-1.5">
          {busy ? <Loader2 size={13} className="animate-spin" /> : null}{filled ? "Link insurance" : "Scan card"}
        </button>
      </div>
      {err ? <div className="text-[11.5px] font-semibold text-[#C8372D] mt-1.5" data-testid="ny-ins-err">{err}</div> : null}
      <div className="text-[10.5px] text-[var(--gray3)] mt-1.5">Scanning fills the fields so you confirm what OCR read — it doesn't commit until you tap Link.</div>
    </div>
  );
}

// ============ Money to post — EOB → ERA remittances (same /eobs data Today's uploads uses) ============
const mSummary = (f) => f.summary || f.extraction_summary || {};
const mAmt = (f) => { const s = mSummary(f); const v = s.check_amount ?? s.total_paid; return isNaN(Number(v)) ? 0 : Number(v); };
const mPayee = (f) => mSummary(f).payee_name || f.payee_name || "Unknown payee";
const mPayer = (f) => mSummary(f).payer_name || f.payer_name || "Unknown payer";
const mCheckNo = (f) => mSummary(f).check_number || "—";
const mClaims = (f) => { const c = mSummary(f).claim_count; return c == null ? null : Number(c); };
const mCheckDate = (f) => mSummary(f).check_date || null;
const mDays = (iso) => { if (!iso) return null; const d = (Date.now() - new Date(iso).getTime()) / 86400000; return Math.floor(d); };
const mFailed = (f) => { const s = (f.status || "").toLowerCase(); return s === "failed" || s === "error"; };
const mProcessing = (f) => { const s = (f.status || "").toLowerCase(); return s === "processing" || s === "queued" || s === "in_progress" || s === "pending"; };
const mDup = (f) => { const d = f.duplicate; return !!(d && d.is_duplicate && !d.is_original); };
const mAuditCounts = (f) => { const ev = (f.era_validation || {}).reconciliation?.summary || {}; const of = ev.claims_census ?? ev.claims_extracted ?? (mClaims(f) || 0); const ag = ev.claims_verified ?? of; return { ag, of, flagged: ev.claims_flagged || 0 }; };
const mAudit = (f) => { const { ag, of, flagged } = mAuditCounts(f); if (of && ag < of) return true; if (flagged > 0) return true; if (f.audit_status && f.audit_status !== "complete") return true; return false; };
const mUnbal = (f) => { const s = mSummary(f); if (s.reconciliation_flagged === true) return true; if (s.reconciliation_verified === false) return true; if (s.reconciliation_status && !["verified", "healed", "balanced"].includes(s.reconciliation_status)) return true; return false; };
const mNorm = (s) => String(s || "").trim().toLowerCase();

function mBucket(f) {
  if (mDup(f)) return "dup";
  if (mFailed(f)) return "failed";
  if (mProcessing(f)) return "inprogress";
  if (f.era_status === "era_generated") return "era";
  if (mUnbal(f)) return "unbalanced";
  if (mAudit(f)) return "audit";
  return "missing"; // completed, no local blocker, 835 not generated → account-level mapping (rendered from mapper-gaps)
}

const CheckChip = ({ date }) => {
  const d = mDays(date);
  if (d == null) return <span className="text-[11px] text-[#9B9B97]">no check date</span>;
  let color = "#9B9B97", label = `${d}d since check`;
  if (d > 365) { const y = Math.floor(d / 365), r = d % 365; color = "#C4302A"; label = `${y}y ${r}d since check`; }
  else if (d > 90) color = "#9A5A00";
  return <span className="text-[11px] font-semibold" style={{ color }}>{label}</span>;
};

const RQRead = ({ text }) => (
  <div className="hidden xl:block w-[min(400px,32%)] flex-shrink-0 min-w-0 rounded-[9px] px-[11px] py-[8px]" style={{ background: "#FCFCFD", border: "1px solid #F1F1F4" }}>
    <div className="flex items-center gap-1 text-[9.5px] uppercase tracking-[.06em] text-[#9B9B97] font-semibold mb-1">
      <img src="/revQ_emblem_black.webp" alt="revQ" className="h-[11px] w-auto object-contain opacity-80" />revQ's read
    </div>
    <div className="text-[11.5px] text-[var(--gray2)] leading-snug">{text}</div>
  </div>
);
const IdCard = ({ f }) => (
  <div className="hidden md:block w-[186px] flex-shrink-0 rounded-[10px] bg-black/[.03] px-3 py-1.5">
    <KVt k="Payer" v={mPayer(f)} /><KVt k="Payee" v={mPayee(f)} /><KVt k="Check" v={mCheckNo(f)} />
  </div>
);
const KVt = ({ k, v, tone }) => (
  <div className="flex items-center justify-between gap-2 py-[3px] min-w-0">
    <span className="text-[10.5px] text-[#9B9B97] font-medium flex-shrink-0">{k}</span>
    <span className={`text-[11.5px] font-semibold truncate min-w-0 text-right ${tone === "r" ? "text-[#C4302A]" : tone === "a" ? "text-[#9A5A00]" : "text-[var(--ink)]"}`} title={typeof v === "string" ? v : undefined}>{v}</span>
  </div>
);
const MoneyB = ({ f, none }) => (
  <div className="text-right w-[104px] flex-shrink-0">
    <div className="text-[9.5px] tracking-[.06em] uppercase text-[#9B9B97] font-semibold">Check amount</div>
    <div className={`tabular-nums ${none ? "text-[11.5px] text-[#9B9B97] font-medium" : "text-[13px] font-bold text-[var(--ink)]"}`}>{none ? "unknown" : money(mAmt(f))}</div>
    <div className="text-[10px] text-[#9B9B97] mt-0.5">{mCheckDate(f) || "—"}</div>
  </div>
);

function MoneyToPost({ eobFiles, eobsReload, navigate, onOpenCheck }) {
  const [age, setAge] = useState("all");
  const [gaps, setGaps] = useState([]);
  const [openReady, setOpenReady] = useState(false);
  const [shutIP, setShutIP] = useState(true);
  const [linkFor, setLinkFor] = useState(null);
  const [linkVal, setLinkVal] = useState("");
  const [linkPrev, setLinkPrev] = useState(false);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({});

  const files = eobFiles || [];
  useEffect(() => { getMapperGaps().then((r) => setGaps(r.gaps)).catch(() => setGaps([])); }, []);

  const inAge = useCallback((f) => {
    if (age === "all") return true;
    const d = mDays(mCheckDate(f)); if (d == null) return false;
    if (age === "a30") return d <= 30;
    if (age === "a90") return d >= 31 && d <= 90;
    if (age === "a365") return d >= 91 && d <= 365;
    if (age === "aover") return d > 365;
    return true;
  }, [age]);

  const b = useMemo(() => {
    const out = { dup: [], unbalanced: [], audit: [], missing: [], era: [], failed: [], inprogress: [] };
    files.filter(inAge).forEach((f) => out[mBucket(f)].push(f));
    return out;
  }, [files, inAge]);

  const ageCounts = useMemo(() => {
    const c = { all: files.length, a30: 0, a90: 0, a365: 0, aover: 0 };
    files.forEach((f) => { const d = mDays(mCheckDate(f)); if (d == null) return; if (d <= 30) c.a30++; else if (d <= 90) c.a90++; else if (d <= 365) c.a365++; else c.aover++; });
    return c;
  }, [files]);

  const received = files.filter((f) => mAmt(f) > 0).reduce((s, f) => s + mAmt(f), 0);
  const readySum = b.era.reduce((s, f) => s + mAmt(f), 0);
  const heldCount = b.dup.length + b.unbalanced.length + b.audit.length + b.missing.length;
  const noAmt = files.filter((f) => mAmt(f) === 0).length;

  const missRows = useMemo(() => {
    const rows = [];
    gaps.forEach((g) => {
      const matched = b.missing.filter((f) => mNorm(mPayer(f)) === mNorm(g.payer) && (!g.payee || mNorm(mPayee(f)) === mNorm(g.payee)));
      if (!matched.length) return;
      const held = matched.reduce((s, f) => s + mAmt(f), 0);
      (g.missing || []).forEach((field) => rows.push({ key: `${field}::${g.payer}::${g.payee || ""}`, field, gap: g, matched, held, count: matched.length, files: g.files }));
    });
    return rows.sort((x, y) => y.held - x.held);
  }, [gaps, b.missing]);
  const missTotal = missRows.reduce((s, r) => s + r.held, 0);

  const agePills = [
    { k: "all", label: "All" },
    { k: "a30", label: "30 days or less" },
    { k: "a90", label: "31–90 days", dot: "#FF9500" },
    { k: "a365", label: "91 days–1 year", dot: "#FF9500" },
    { k: "aover", label: "Over a year", dot: "#FF3B30" },
  ];
  const pill = (active) => `text-[11.5px] font-semibold rounded-full px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${active ? "bg-[#141412] text-white border-[#141412]" : "bg-white text-[var(--gray2)] border-black/[.1] hover:border-black/[.25]"}`;
  const bandHead = { background: "#FAFAFB", borderTop: "1px solid #F1F1F4", borderBottom: "1px solid #F1F1F4" };

  const missLabel = { edi_payer_id: "Payer EDI ID missing", ehi_client_id: "Client ID missing", payee_npi: "Payee NPI missing" };
  const missField = { edi_payer_id: "EDI payer ID", ehi_client_id: "EHI client ID", payee_npi: "Payee NPI" };
  const missPh = { edi_payer_id: "1–12 letters or digits", ehi_client_id: "4 digits", payee_npi: "10 digits · checksum validated" };
  const missEnt = (r) => r.field === "edi_payer_id" ? r.gap.payer : (r.gap.payee || mPayee(r.matched[0]));

  const doLink = async (r) => {
    const val = linkVal.trim(); setBusy(r.key);
    try {
      if (r.field === "edi_payer_id") await linkPayer(r.gap.payer, val);
      else if (r.field === "payee_npi") await linkPayeeNpi(missEnt(r), val);
      else await linkPayee(missEnt(r), val);
      toast.success(`Linked ${missEnt(r)} · released ${r.count} held`);
      setLinkFor(null); setLinkVal(""); setLinkPrev(false);
      await eobsReload?.(); getMapperGaps().then((x) => setGaps(x.gaps)).catch(() => {});
    } catch (ex) { toast.error(describeError(ex)); } finally { setBusy(""); }
  };
  const doDownload = async (f) => {
    try {
      const { available, text } = await getEra(f.file_id);
      if (!available) { toast.message("The 835 hasn't generated yet."); return; }
      const blob = new Blob([text], { type: "text/plain" }); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${(mPayer(f) || "era").replace(/\W+/g, "_")}_835.txt`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the 835."); }
  };
  const doRecovery = async (f) => {
    setBusy(f.file_id);
    try { await runDeepRecovery(f.file_id); toast.success("Deep recovery started"); await eobsReload?.(); }
    catch (ex) { toast.error(describeError(ex)); } finally { setBusy(""); }
  };

  const cap = (arr, id) => expanded[id] ? arr : arr.slice(0, 3);
  const MoreBtn = ({ id, total }) => total > 3 ? (
    <button onClick={() => setExpanded((e) => ({ ...e, [id]: !e[id] }))} data-testid={`mtp-more-${id}`} className="w-full text-[11.5px] font-semibold text-[var(--gray2)] hover:text-[var(--ink)] py-2.5 text-center transition" style={{ borderBottom: "1px solid #F1F1F4" }}>
      {expanded[id] ? "Show less" : `Show ${total - 3} more`}
    </button>
  ) : null;

  const Row = ({ f, finding, tone, detail, read, verb }) => (
    <div className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F1F1F4" }} data-testid={`mtp-row-${f.file_id}`}>
      <div className="flex-1 min-w-0 max-w-[240px]">
        <div className="text-[12.5px] font-bold truncate" style={{ color: tone === "r" ? "#C4302A" : tone === "a" ? "#9A5A00" : "var(--ink)" }}>{finding}</div>
        <div className="text-[11px] text-[#9B9B97] truncate">{f.filename}</div>
        <div className="mt-0.5"><CheckChip date={mCheckDate(f)} /></div>
      </div>
      <IdCard f={f} />
      <div className="hidden lg:block w-[150px] flex-shrink-0 rounded-[10px] bg-black/[.03] px-3 py-1.5">{detail}</div>
      <RQRead text={read} />
      <MoneyB f={f} />
      {verb}
    </div>
  );
  const OpenBtn = (f) => (
    <button data-testid={`mtp-open-${f.file_id}`} onClick={() => onOpenCheck?.(f)} style={{ minWidth: "84px", border: "1px solid #E9E9EC", color: "#141412" }} className="text-[11.5px] font-semibold bg-transparent rounded-[9px] px-3.5 py-2 hover:border-[#9B9B97] transition flex-shrink-0">Open</button>
  );

  const BandHeadRow = ({ testid, caretColor, titleColor, title, count, chipBg, chipColor, right, extra }) => (
    <div className="w-full flex items-center gap-2 px-3.5 py-2 mt-2" style={bandHead} data-testid={testid}>
      <span style={{ color: caretColor, fontSize: "11px" }}>▾</span>
      <span className="text-[12.5px] font-bold" style={{ color: titleColor }}>{title}</span>
      <span className="text-[11px] font-bold rounded-full px-2 py-[1px]" style={{ background: chipBg, color: chipColor }}>{count}</span>
      {extra}
      <span className="flex-1" />
      <span className="text-[11px] text-[#9B9B97]">{right}</span>
    </div>
  );

  return (
    <div data-testid="mtp-root">
      {/* age pills — measured from the check date */}
      <div className="flex items-center gap-2 flex-wrap mb-2.5" data-testid="mtp-pills">
        {agePills.map((p) => (
          <button key={p.k} onClick={() => setAge(p.k)} data-testid={`mtp-age-${p.k}`} className={pill(age === p.k)}>
            {p.dot ? <span className="w-[6px] h-[6px] rounded-full" style={{ background: p.dot }} /> : null}
            {p.label} <b>{ageCounts[p.k]}</b>
          </button>
        ))}
        <span className="text-[11px] text-[#9B9B97] ml-1">measured from the check date, not the upload</span>
      </div>

      {/* context bar */}
      <div className="flex items-center gap-2 mb-2 text-[12px] flex-wrap" data-testid="mtp-context">
        <span className="font-semibold text-[var(--ink)]">{files.length} remittance{files.length === 1 ? "" : "s"}</span>
        <span className="text-[#C7C7CC]">·</span>
        <span><b className="text-[var(--ink)]">{money(received)}</b> <span className="text-[#9B9B97]">read from checks</span></span>
        <span className="text-[#C7C7CC]">·</span>
        <span><b className="text-[var(--ink)]">{money(readySum)}</b> <span className="text-[#9B9B97]">ready to post</span></span>
        {heldCount > 0 ? <><span className="text-[#C7C7CC]">·</span><span className="font-semibold" style={{ color: "#C4302A" }}>{heldCount} held</span></> : null}
        {noAmt > 0 ? <><span className="text-[#C7C7CC]">·</span><span className="text-[#9B9B97]">{noAmt} with no amount yet</span></> : null}
      </div>

      {files.length === 0 ? <div className="text-[12px] text-[var(--gray2)] py-6 text-center" data-testid="mtp-empty">No remittances in this view.</div> : null}

      {/* 1 · Duplicates */}
      {b.dup.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-dup">
          <BandHeadRow testid="mtp-bandhead-dup" caretColor="#C4302A" titleColor="#C4302A" title="Duplicates" count={b.dup.length} chipBg="#FDEDEC" chipColor="#C4302A" right="Compare and delete one" />
          {cap(b.dup, "dup").map((f) => { const { ag, of } = mAuditCounts(f); return Row({
            f, finding: `Duplicate ${f.duplicate?.instance_index || 2} of ${f.duplicate?.instance_count || 2}`, tone: "r",
            detail: <><KV k="Claims" v={mClaims(f) != null ? `${mClaims(f)}` : "—"} /><KV k="Audit" v={of ? `${ag} of ${of}` : "—"} tone={of && ag < of ? "a" : undefined} /></>,
            read: "Same check number and amount as another file in this list. Posting both would apply the payment twice.",
            verb: OpenBtn(f),
          }); })}
          <MoreBtn id="dup" total={b.dup.length} />
        </div>
      ) : null}

      {/* 2 · Unbalanced */}
      {b.unbalanced.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-unbalanced">
          <BandHeadRow testid="mtp-bandhead-unbalanced" caretColor="#C4302A" titleColor="#C4302A" title="Unbalanced" count={b.unbalanced.length} chipBg="#FDEDEC" chipColor="#C4302A" right="Open the check" />
          {cap(b.unbalanced, "unbalanced").map((f) => Row({
            f, finding: `Residual ${money(Math.abs(mSummary(f).unbalanced_amount || 0))}`, tone: "r",
            detail: <><KV k="Claims" v={mClaims(f) != null ? `${mClaims(f)}` : "—"} /><KV k="Lines" v={`${mSummary(f).imbalanced_line_count ?? mSummary(f).service_line_count ?? "—"}`} /></>,
            read: "The claim lines do not reconcile to the check total. revQ can release the gate, but someone has to agree the residual is acceptable first.",
            verb: OpenBtn(f),
          }))}
          <MoreBtn id="unbalanced" total={b.unbalanced.length} />
        </div>
      ) : null}

      {/* 3 · Audit needs review */}
      {b.audit.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-audit">
          <BandHeadRow testid="mtp-bandhead-audit" caretColor="#9A5A00" titleColor="#9A5A00" title="Audit needs review" count={b.audit.length} chipBg="#FDF4E5" chipColor="#9A5A00" right="Open the check" />
          {cap(b.audit, "audit").map((f) => { const { ag, of } = mAuditCounts(f); return Row({
            f, finding: `Audit disagrees on ${Math.max(0, of - ag)} of ${of}`, tone: "a",
            detail: <><KV k="Claims" v={mClaims(f) != null ? `${mClaims(f)}` : "—"} /><KV k="Agrees" v={`${ag} of ${of}`} tone="a" /></>,
            read: "An independent pass re-censused the EOB and disagrees with the extraction. The count is file-level, so read the source against the claims.",
            verb: OpenBtn(f),
          }); })}
          <MoreBtn id="audit" total={b.audit.length} />
        </div>
      ) : null}

      {/* 4 · Missing for 835P — account-level mappings from /profiler/mapper-gaps */}
      {missRows.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-missing">
          <BandHeadRow testid="mtp-bandhead-missing" caretColor="#C4302A" titleColor="#C4302A" title="Missing for 835P" count={b.missing.length} chipBg="#FDEDEC" chipColor="#C4302A" right="Link once · clears future remittances"
            extra={<span className="text-[11.5px] font-semibold" style={{ color: "#6C6C69" }}>{money(missTotal)} · {missRows.length} mapping{missRows.length === 1 ? "" : "s"} to link</span>} />
          {cap(missRows, "missing").map((r) => (
            <div key={r.key} data-testid={`mtp-miss-${r.key}`}>
              <div className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F1F1F4" }}>
                <div className="flex-1 min-w-0 max-w-[240px]">
                  <div className="text-[12.5px] font-bold" style={{ color: "#C4302A" }}>Missing {r.field}</div>
                  <div className="text-[11px] mt-0.5 text-[#9B9B97] truncate">{missEnt(r)} · {r.count} held · {r.files} on file</div>
                </div>
                <div className="hidden md:block w-[186px] flex-shrink-0 rounded-[10px] bg-black/[.03] px-3 py-1.5"><KV k="Claims" v={mClaims(r.matched[0]) != null ? `${mClaims(r.matched[0])}` : "—"} /><KV k="Scope" v="account-level" /></div>
                <RQRead text={`The 835 cannot be built without ${r.field}. These are account mappings, so linking once clears every future remittance too.`} />
                <div className="text-right w-[104px] flex-shrink-0"><div className="text-[9.5px] tracking-[.06em] uppercase text-[#9B9B97] font-semibold">Held</div><div className="text-[13px] font-bold text-[var(--ink)] tabular-nums">{money(r.held)}</div><div className="text-[10px] text-[#9B9B97] mt-0.5">{r.count} remit</div></div>
                <button data-testid={`mtp-link-${r.key}`} onClick={() => { setLinkFor(r.key); setLinkVal(""); setLinkPrev(false); }} style={{ minWidth: "84px" }} className="text-[11.5px] font-bold text-white bg-[var(--ink)] rounded-[9px] px-3.5 py-2 inline-flex items-center justify-center gap-1.5 flex-shrink-0"><Link2 size={13} /> Link</button>
              </div>
              {linkFor === r.key && !linkPrev ? (
                <div className="bg-black/[.03] rounded-[12px] p-3.5 mb-2 flex items-end gap-2 flex-wrap" data-testid={`mtp-linkbar-${r.key}`}>
                  <div className="flex-1 min-w-[220px]"><label className="text-[10px] font-semibold text-[var(--gray3)] uppercase">{missField[r.field]}</label>
                    <input value={linkVal} onChange={(e) => setLinkVal(e.target.value)} placeholder={missPh[r.field]} data-testid={`mtp-linkinput-${r.key}`} className="w-full mt-1 px-3 py-2 rounded-[9px] border border-black/[.12] text-[12.5px] bg-white" /></div>
                  <span className="flex-1" /><span className="text-[10.5px] text-[var(--gray3)]">applies to {missEnt(r)} across the whole account</span>
                  <button onClick={() => { setLinkFor(null); setLinkVal(""); }} className="text-[11.5px] font-semibold text-[var(--gray2)] px-3 py-2">Cancel</button>
                  <button onClick={() => setLinkPrev(true)} disabled={!linkVal.trim()} data-testid={`mtp-linkreview-${r.key}`} className="text-[12px] font-bold text-white bg-[var(--ink)] rounded-[10px] px-4 py-2 disabled:opacity-40">Review the change</button>
                </div>
              ) : null}
              {linkFor === r.key && linkPrev ? (
                <div className="rounded-[12px] p-3.5 mb-2 border" style={{ background: "#FFF8F0", borderColor: "#F0D9BE" }} data-testid={`mtp-linkprev-${r.key}`}>
                  <div className="text-[11px] font-bold text-[var(--ink)] mb-1">What this does</div>
                  <div className="text-[12px] text-[var(--gray2)]">Maps <b className="text-[var(--ink)]">{missEnt(r)}</b> to <b className="text-[var(--ink)]">{linkVal.trim()}</b>, releasing this remittance and {Math.max(0, r.files - 1)} other{r.files - 1 === 1 ? "" : "s"} held on the same field.</div>
                  <div className="text-[11px] mt-2 rounded-[8px] px-2.5 py-2" style={{ background: "#FBEEDD", color: "#9A5A00" }}>POST /profiler/{r.field === "edi_payer_id" ? "payer-link" : r.field === "payee_npi" ? "payee-npi-link" : "payee-link"} writes at the account level, not on this file. Every future remittance from {missEnt(r)} will use it too.</div>
                  <div className="flex items-center gap-2 mt-3"><span className="flex-1" />
                    <button onClick={() => setLinkPrev(false)} className="text-[11.5px] font-semibold text-[var(--gray2)] px-3 py-2">Back</button>
                    <button onClick={() => doLink(r)} disabled={busy === r.key} data-testid={`mtp-linkcommit-${r.key}`} className="text-[12px] font-bold text-white bg-[var(--ink)] rounded-[10px] px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">{busy === r.key ? <Loader2 size={13} className="animate-spin" /> : null}Link it</button>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
          <MoreBtn id="missing" total={missRows.length} />
        </div>
      ) : null}

      {/* 5 · ERA generated */}
      {b.era.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-era">
          <BandHeadRow testid="mtp-bandhead-era" caretColor="#1f9d57" titleColor="#1f7a46" title="ERA generated" count={b.era.length} chipBg="#E7F6EE" chipColor="#1f7a46" right="Hand off to Payment Posting"
            extra={<span className="text-[11.5px] font-bold" style={{ color: "#1f7a46" }}>{money(readySum)}</span>} />
          {cap(b.era, "era").map((f) => { const { ag, of } = mAuditCounts(f); return Row({
            f, finding: "835 generated", tone: undefined,
            detail: <><KV k="Claims" v={mClaims(f) != null ? `${mClaims(f)}` : "—"} /><KV k="Audit" v={of ? `${ag} of ${of}` : "—"} /></>,
            read: "Parsed, balanced, audited and built an 835. Nothing is holding it.",
            verb: OpenBtn(f),
          }); })}
          <MoreBtn id="era" total={b.era.length} />
        </div>
      ) : null}

      {/* 6 · Failed */}
      {b.failed.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-failed">
          <BandHeadRow testid="mtp-bandhead-failed" caretColor="#9B9B97" titleColor="var(--ink)" title="Failed" count={b.failed.length} chipBg="#F1F1F4" chipColor="#6C6C69" right="Recovery pass" />
          {cap(b.failed, "failed").map((f) => (
            <div key={f.file_id} className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F1F1F4" }} data-testid={`mtp-failed-${f.file_id}`}>
              <div className="flex-1 min-w-0 max-w-[240px]"><div className="text-[12.5px] font-bold" style={{ color: "#C4302A" }}>Extraction failed</div><div className="text-[11px] text-[#9B9B97] truncate">{f.filename}</div></div>
              <div className="hidden md:block w-[186px] flex-shrink-0 rounded-[10px] bg-black/[.03] px-3 py-1.5"><KV k="Payer" v="not read" /><KV k="Amount" v="not read" /></div>
              <RQRead text="The first extraction pass could not read this remittance. A heavier recovery pass is available." />
              <MoneyB f={f} none />
              <button onClick={() => doRecovery(f)} disabled={busy === f.file_id} data-testid={`mtp-recovery-${f.file_id}`} style={{ minWidth: "84px", border: "1px solid #E9E9EC", color: "#141412" }} className="text-[11.5px] font-semibold bg-transparent rounded-[9px] px-3.5 py-2 hover:border-[#9B9B97] transition flex-shrink-0">{busy === f.file_id ? "…" : "Deep recovery"}</button>
            </div>
          ))}
          <MoreBtn id="failed" total={b.failed.length} />
        </div>
      ) : null}

      {/* 7 · In Progress — last, no verb */}
      {b.inprogress.length > 0 ? (
        <div className="mb-2" data-testid="mtp-band-inprogress">
          <button onClick={() => setShutIP((v) => !v)} className="w-full flex items-center gap-2 px-3.5 py-2 mt-2" style={bandHead} data-testid="mtp-bandhead-inprogress">
            <span style={{ color: "#9B9B97", fontSize: "11px", display: "inline-block", transform: shutIP ? "rotate(-90deg)" : "none", transition: "transform .15s" }}>▾</span>
            <span className="text-[12.5px] font-bold text-[var(--ink)]">In Progress</span>
            <span className="text-[11px] font-bold rounded-full px-2 py-[1px]" style={{ background: "#F1F1F4", color: "#6C6C69" }}>{b.inprogress.length}</span>
            <span className="flex-1" /><span className="text-[11px] text-[#9B9B97]">still extracting</span>
          </button>
          {!shutIP ? b.inprogress.map((f) => (
            <div key={f.file_id} className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F1F1F4" }} data-testid={`mtp-ip-${f.file_id}`}>
              <div className="flex-1 min-w-0"><div className="text-[12.5px] font-semibold text-[var(--ink)] truncate">{f.filename}</div><div className="text-[11px] text-[#9B9B97]">still processing — nothing to do</div></div>
              <span className="text-[11px] text-[#9B9B97]">processing</span>
            </div>
          )) : null}
        </div>
      ) : null}

      <div className="text-[10.5px] text-[var(--gray3)] mt-3 pt-2 border-t border-black/[.06] leading-relaxed" data-testid="mtp-foot">
        Source <code>/api/edi/eobs</code>. Band names are a client-side classifier over the real status enum, <code>duplicate</code>, <code>era_validation</code> and <code>/profiler/mapper-gaps</code> — not an API field. Aging is measured from <code>extraction_summary.check_date</code>.
      </div>
    </div>
  );
}

function NeedsYouToday({ enriched, navigate, onFixInsurance, onOpenClaim, notesReload, eobFiles, eobsReload, onOpenCheck }) {
  const [seg, setSeg] = useState("claims");
  const [grp, setGrp] = useState("everything");
  const [sel, setSel] = useState("all");
  const [sort, setSort] = useState("auto");
  const [cap, setCap] = useState(5);
  const [openBand, setOpenBand] = useState("no_payer");
  const [agingOnly, setAgingOnly] = useState(false);
  const [expand, setExpand] = useState(null); // patient key for T1/T2

  const base = useMemo(() => enriched.filter(({ n, r }) => clusterOf(n, r)), [enriched]);
  const items = useMemo(() => (agingOnly ? base.filter(({ n }) => isAging(n)) : base), [base, agingOnly]);
  const agingCount = useMemo(() => base.filter(({ n }) => isAging(n)).length, [base]);
  // claim-based counts
  const cannotCount = useMemo(() => base.filter(({ n, r }) => BLOCKED_BANDS.has(clusterOf(n, r))).length, [base]);
  const totalVal = useMemo(() => base.reduce((s, { n }) => s + estCharge(n), 0), [base]);
  useEffect(() => {
    const bandTotal = base.reduce((acc, { n, r }) => { acc[clusterOf(n, r)] = (acc[clusterOf(n, r)] || 0) + 1; return acc; }, {});
    const sum = Object.values(bandTotal).reduce((a, b) => a + b, 0);
    if (process.env.NODE_ENV !== "production" && sum !== base.length) {
      // eslint-disable-next-line no-console
      console.error(`[NeedsYouToday] header items (${base.length}) !== sum(band counts) (${sum})`);
    }
  }, [base]);

  const bandSort = (bandKey) => (a, b) => {
    if (sort === "val") return b.est - a.est;
    if (sort === "claims") return b.count - a.count;
    if (sort === "auto" && bandKey === "review") return (b.rep.n.claim_readiness?.denial_risk === "medium" ? 1 : 0) - (a.rep.n.claim_readiness?.denial_risk === "medium" ? 1 : 0);
    return b.est - a.est;
  };

  // Per-category claim counts + money (for pills + context bar).
  const cats = useMemo(() => NY_CLUSTERS.map((c) => {
    const rows = base.filter(({ n, r }) => clusterOf(n, r) === c.key);
    return { key: c.key, label: c.key === "review" ? "Coding review" : c.label, tone: c.tone, claims: rows.length, val: rows.reduce((s, { n }) => s + estCharge(n), 0), aged: rows.filter(({ n }) => isAging(n)).length };
  }).filter((c) => c.claims), [base]);
  const selCat = cats.find((c) => c.key === sel);
  const selAged = sel === "all" ? agingCount : (selCat?.aged || 0);

  const groups = useMemo(() => {
    let gs;
    if (sel !== "all") {
      gs = [{ key: sel, label: "", tone: BLOCKED_BANDS.has(sel) ? "blk" : "wrn", patients: collapsePatients(items.filter(({ n, r }) => clusterOf(n, r) === sel)) }];
    } else if (grp === "payer") {
      const m = {}; items.forEach((it) => { const k = payerOf(it.n) || "No payer on file"; (m[k] ||= []).push(it); });
      gs = Object.keys(m).sort().map((label) => ({ key: label, label, tone: "blk", patients: collapsePatients(m[label]) }));
    } else if (grp === "prov") {
      const m = {}; items.forEach((it) => { const k = it.n.provider_name || "Unassigned provider"; (m[k] ||= []).push(it); });
      gs = Object.keys(m).sort().map((label) => ({ key: label, label, tone: "blk", patients: collapsePatients(m[label]) }));
    } else {
      gs = NY_CLUSTERS.map((c) => ({ ...c, patients: collapsePatients(items.filter(({ n, r }) => clusterOf(n, r) === c.key)) })).filter((g) => g.patients.length);
    }
    gs.forEach((g) => g.patients.sort(bandSort(g.key)));
    return gs;
  }, [items, grp, sel, sort]);

  const showHeads = sel === "all" && grp === "everything";
  const pill = (active) => `text-[11.5px] font-semibold rounded-full px-3 py-1.5 border transition inline-flex items-center gap-1.5 ${active ? "bg-[#141412] text-white border-[#141412]" : "bg-white text-[var(--gray2)] border-black/[.1] hover:border-black/[.25]"}`;

  const eobList = eobFiles || [];
  const mReceived = eobList.filter((f) => mAmt(f) > 0).reduce((s, f) => s + mAmt(f), 0);
  const mReady = eobList.filter((f) => mBucket(f) === "ready").reduce((s, f) => s + mAmt(f), 0);
  const segTab = (on) => ({ padding: "12px 2px 11px", borderBottom: "2px solid transparent", marginBottom: "-1px", color: on ? "#141412" : "#6E6E6B", borderBottomColor: on ? "#141412" : "transparent" });
  const segSub = (on) => ({ fontSize: "11.5px", color: on ? "#6E6E6B" : "#9B9B97" });

  return (
    <div className="glass-surface rounded-[20px] p-5" data-testid="home-needsyou">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="text-[14px] font-bold text-[var(--ink)]">Needs you today</div>
        <span className="flex-1" />
        <SortViewMenu testid="ny-sortview" groupVal={grp} onGroup={setGrp} sortVal={sort} onSort={setSort}
          groupOpts={[{ v: "everything", label: "Everything" }, { v: "payer", label: "One payer" }, { v: "prov", label: "One provider" }]}
          sortOpts={[{ v: "auto", label: "Smart — per group" }, { v: "val", label: "Highest value" }, { v: "claims", label: "Most claims" }]}
          extra={{ title: "Rows per group", val: cap, onVal: setCap, opts: [{ v: 3, label: "Top 3" }, { v: 5, label: "Top 5" }, { v: 0, label: "All — long scroll" }] }} />
        <button onClick={() => navigate(seg === "money" ? "/payments" : "/create-claims")} className="text-[11.5px] font-semibold hover:underline" style={{ color: "#5856D6" }} data-testid="home-work-queue">{seg === "money" ? "Payments & ERA →" : "Work the queue →"}</button>
      </div>

      {/* segment toggle — claims to work vs money to post (underline tabs) */}
      <div className="flex mb-3" style={{ gap: "26px", padding: "0 18px", borderBottom: "1px solid #F1F1F4" }} data-testid="ny-segstrip">
        <button onClick={() => setSeg("claims")} data-testid="ny-seg-claims" className="flex items-baseline gap-2 transition-colors hover:text-[#141412]" style={segTab(seg === "claims")}>
          <span style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "-.005em" }}>Claims to work</span>
          <span className="tabular-nums" style={segSub(seg === "claims")}>{base.length}</span>
          <span className="tabular-nums" style={segSub(seg === "claims")}>· {money(totalVal)}</span>
        </button>
        <button onClick={() => setSeg("money")} data-testid="ny-seg-money" className="flex items-baseline gap-2 transition-colors hover:text-[#141412]" style={segTab(seg === "money")}>
          <span style={{ fontSize: "13px", fontWeight: 600, letterSpacing: "-.005em" }}>Money to post</span>
          <span className="tabular-nums" style={segSub(seg === "money")}>{eobList.length}</span>
          <span className="tabular-nums" style={segSub(seg === "money")}>· {money(mReceived)}</span>
        </button>
      </div>

      {seg === "money" ? (
        <MoneyToPost eobFiles={eobFiles} eobsReload={eobsReload} navigate={navigate} onOpenCheck={onOpenCheck} />
      ) : (
      <>
      {/* pill row */}
      <div className="flex items-center gap-2 flex-wrap mb-2.5" data-testid="ny-pills">
        <button onClick={() => setSel("all")} data-testid="ny-pill-all" className={pill(sel === "all")}>All <b>{base.length}</b></button>
        {cats.map((c) => (
          <button key={c.key} onClick={() => setSel(c.key)} data-testid={`ny-pill-${c.key}`} className={pill(sel === c.key)}>
            <span className="w-[6px] h-[6px] rounded-full" style={{ background: c.tone === "wrn" ? "#FF9500" : "#FF3B30" }} />
            {c.label} <b>{c.claims}</b>
          </button>
        ))}
      </div>

      {/* context bar */}
      <div className="flex items-center gap-2 mb-2 text-[12px]" data-testid="ny-context">
        {sel === "all" ? (
          <><span className="font-semibold text-[var(--ink)]">{base.length} claims across {cats.length} categories · {money(totalVal)} expected</span><span className="flex-1" /><span className="text-[#9B9B97]">pick a category to work a batch</span></>
        ) : (
          <>
            <span className="font-semibold text-[var(--ink)]">{selCat?.claims} claims · {money(selCat?.val || 0)} expected · </span>
            <span className="font-semibold" style={{ color: BLOCKED_BANDS.has(sel) ? "#C4302A" : "#9A5A00" }}>{BLOCKED_BANDS.has(sel) ? "cannot be submitted" : "can be submitted"}</span>
            <span className="flex-1" />
            <button data-testid="ny-batch" onClick={() => { const gp = groups[0]?.patients || []; sel === "no_payer" && gp[0] ? onFixInsurance(gp[0].notes) : sel === "review" ? onOpenClaim(gp.map((p) => p.rep.n), 0) : navigate("/create-claims"); }}
              className="font-semibold hover:underline" style={{ color: "#4B4BBF" }}>
              {sel === "no_payer" ? `Fix all ${selCat?.claims}` : sel === "review" ? `Review ${selCat?.claims}` : `Open ${selCat?.claims}`} →
            </button>
          </>
        )}
      </div>

      {sel !== "no_dos" && selAged > 0 ? (
        <button onClick={() => setAgingOnly((v) => !v)} data-testid="ny-aging-filter"
          className="w-full flex items-center gap-2 rounded-[11px] px-3.5 py-2 mb-2 text-[12px] font-semibold transition"
          style={agingOnly ? { background: "#C4302A", color: "#fff" } : { background: "#FDEDEC", color: "#C4302A" }}>
          <span>{selAged} claims are {AGING_DAYS} or more days past their date of service</span>
          <span className="flex-1" />
          <span>{agingOnly ? "Clear filter ✕" : "Show only these →"}</span>
        </button>
      ) : null}

      {groups.length === 0 ? (
        <div className="text-[12px] text-[var(--gray2)] py-4 text-center" data-testid="ny-empty">Nothing needs you — the queue is clean.</div>
      ) : groups.map((g) => {
        const bandClaims = g.patients.reduce((s, p) => s + p.count, 0);
        const bandVal = g.patients.reduce((s, p) => s + p.est, 0);
        const isOpen = !showHeads || openBand === g.key;
        const shown = cap ? g.patients.slice(0, cap) : g.patients;
        const shownClaims = shown.reduce((s, p) => s + p.count, 0);
        const hiddenClaims = bandClaims - shownClaims;
        const hiddenVal = g.patients.slice(shown.length).reduce((s, p) => s + p.est, 0);
        const isPayerBand = g.key === "no_payer";
        const isReview = g.key === "review" || g.tone === "wrn";
        const lblColor = isReview ? "#9A5A00" : "#C4302A";
        const pillBg = isReview ? "#FDF4E5" : "#FDEDEC";
        const verb = g.key === "no_payer" ? `Fix all ${bandClaims}` : isReview ? `Review ${bandClaims}` : `Open ${bandClaims}`;
        return (
          <div key={g.key} className="mb-2 last:mb-0" data-testid={`ny-band-${g.key}`}>
            {showHeads ? (
            <button onClick={() => setOpenBand(isOpen ? null : g.key)} data-testid={`ny-bandhead-${g.key}`}
              className="w-full flex items-center gap-2 px-3.5 py-2 mt-2" style={{ background: "#FAFAFB", borderTop: "1px solid #F1F1F4", borderBottom: "1px solid #F1F1F4" }}>
              <span style={{ color: "#9B9B97", fontSize: "11px", display: "inline-block", transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform .15s" }}>▾</span>
              <span className="text-[12.5px] font-bold" style={{ color: lblColor }}>{g.label}</span>
              <span className="text-[11px] font-bold rounded-full px-2 py-[1px]" style={{ background: pillBg, color: lblColor }} data-testid={`ny-bandcount-${g.key}`}>{bandClaims}</span>
              {bandVal > 0 ? <span className="text-[11.5px] font-semibold" style={{ color: "#6C6C69" }}>{money(bandVal)} expected</span> : null}
              <span className="flex-1" />
              <span onClick={(e) => { e.stopPropagation(); isPayerBand && g.patients[0] ? onFixInsurance(g.patients[0].notes) : (isReview ? onOpenClaim(g.patients.map((p) => p.rep.n), 0) : navigate("/create-claims")); }}
                className="text-[11.5px] font-semibold hover:underline" style={{ color: "#4B4BBF" }}>{verb} →</span>
            </button>
            ) : null}

            {isOpen ? (
              <>
                {shown.map((p) => {
                  const n = p.rep.n, r = p.rep.r, cluster = clusterOf(n, r);
                  const cr = n.claim_readiness || {};
                  const payer = payerOf(n);
                  const age = claimAgeDays(p.earliestDos);
                  const aged = age != null && age >= AGING_DAYS;
                  const flaggedIcd = (n.icd_codes || []).filter(icdFlagged).length;
                  const v = claimView(n);
                  const extraBlockers = blockersOf(n, r).size - 1;
                  const rowExpanded = expand === p.key;
                  return (
                    <div key={p.key} data-testid={`ny-row-${p.key}`}>
                      <div className="flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F1F1F4" }} onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAFC")} onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                        <div className="flex-1 min-w-0 max-w-[330px]">
                          <div className="text-[12.5px] font-bold text-[var(--ink)] truncate flex items-center gap-1.5">
                            {p.name}{p.count > 1 ? <span className="text-[9px] font-bold rounded-full px-1.5 py-[1px]" style={{ background: "#EDEDFB", color: "#5856D6" }}>{p.count} claims</span> : null}
                            {extraBlockers > 0 ? <span className="text-[9px] font-semibold rounded-full px-1.5 py-[1px]" style={{ background: "#FDEDEC", color: "#C4302A" }} data-testid={`ny-moreblk-${p.key}`}>+{extraBlockers} more blocker{extraBlockers > 1 ? "s" : ""}</span> : null}
                          </div>
                          {p.count > 1 ? <div className="text-[11px] mt-0.5 text-[#9B9B97]">one fix clears all {p.count}</div> : null}
                        </div>
                        <div className="hidden md:block w-[176px] flex-shrink-0 rounded-[10px] bg-black/[.03] px-3 py-1.5">
                          <KV k={p.count > 1 ? `${p.count} visits, earliest` : "Visit"} v={p.earliestDos || (cluster === "no_dos" ? "no date of service" : "—")} tone={cluster === "no_dos" ? "r" : ""} />
                          <div className="flex items-center justify-between gap-2 py-[3px]">
                            <span className="text-[10.5px] text-[#9B9B97] font-medium truncate min-w-0">MRN</span>
                            <span className="text-[11.5px] font-semibold text-[var(--ink)] whitespace-nowrap flex-shrink-0">{n.mrn || "—"} · <span style={aged ? { color: "#C4302A", fontWeight: 700 } : {}}>{age != null ? `${age}d old` : "—"}</span></span>
                          </div>
                        </div>
                        <div className="hidden lg:flex flex-col justify-center gap-[3px] w-[218px] flex-shrink-0 rounded-[9px] px-[11px] py-[7px] relative group" style={{ background: "#FCFCFD", border: "1px solid #F1F1F4" }} tabIndex={0} data-testid={`ny-summary-${p.key}`}>
                          {cluster === "review" ? (
                            <><KV k="Payer" v={payer || "none on file"} tone={payer ? "" : "r"} /><KV k="Denial risk" v={cr.denial_risk || "not scored"} tone={cr.denial_risk === "medium" ? "a" : ""} /></>
                          ) : cluster === "code" ? (
                            <><KV k="Payer" v={payer || "none on file"} tone={payer ? "" : "r"} /><KV k="Blockers" v={`${extraBlockers + 1} blocker${extraBlockers ? "s" : ""} · ${v.flagged} to review`} tone="r" /></>
                          ) : cluster === "no_dos" ? (
                            <><KV k="Payer" v={payer || "none on file"} tone={payer ? "" : "r"} /><KV k="Codes" v={`${v.lines.length} CPT · ${v.dx.length} ICD`} /></>
                          ) : (
                            <><KV k="Lines" v={`${v.lines.length} CPT · ${v.dx.length} Dx`} /><KV k="Edits" v={v.flagged ? `${v.flagged} flagged` : "none"} tone={v.flagged ? "a" : ""} /></>
                          )}
                          <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-[330px] bg-white rounded-[11px] border border-[#E9E9EC] shadow-[0_10px_32px_rgba(20,20,18,.15)] opacity-0 pointer-events-none group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"><ClaimDetail v={v} /></div>
                        </div>
                        <ChipStrip v={v} />
                        <div className="text-right w-[96px] flex-shrink-0">
                          <div className="text-[10.5px] text-[#9B9B97] font-medium">Expected</div>
                          <div className="text-[13px] font-bold text-[var(--ink)] tabular-nums">{p.est ? money(p.est) : "not estimated"}</div>
                        </div>
                        <button data-testid={`ny-fix-${p.key}`}
                          onClick={() => { if (cluster === "no_dos" || cluster === "recode") { onOpenClaim([n], 0); } else { setExpand(rowExpanded ? null : p.key); } }}
                          style={{ minWidth: "70px", border: "1px solid #E9E9EC", color: "#141412" }}
                          className="text-[11.5px] font-semibold bg-transparent rounded-[9px] px-3.5 py-2 transition flex-shrink-0 hover:border-[#9B9B97]"
                          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#9B9B97")}
                          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E9E9EC")}>
                          {cluster === "code" || cluster === "review" ? "Review" : cluster === "no_dos" || cluster === "recode" ? "Open claim" : "Fix"}
                        </button>
                      </div>

                      {rowExpanded && cluster === "no_payer" ? (
                        <InsuranceStrip notes={p.notes} count={p.count} onReload={notesReload} onClose={() => setExpand(null)} />
                      ) : null}

                      {rowExpanded && (cluster === "code" || cluster === "review") ? (
                        <div className="bg-black/[.03] rounded-[12px] p-3.5 mb-2" data-testid={`ny-expand-${p.key}`}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="text-[11px] font-bold text-[var(--ink)]">revQ suggestions · {gatherSuggestions(n).length}</div>
                            <span className="text-[9.5px] font-semibold rounded-full px-2 py-[1.5px]" style={{ background: "#F1F1F4", color: "#9B9B97" }}>preview — apply in the claim form</span>
                          </div>
                          {gatherSuggestions(n).length ? gatherSuggestions(n).map((s, i) => (
                            <div key={i} className="flex items-start gap-3 py-2 border-t border-black/[.06] first:border-t-0">
                              <span className="text-[11px] font-bold rounded-[6px] px-2 py-[2px] bg-white border border-black/[.1]">{s.code}</span>
                              <div className="flex-1 min-w-0"><div className="text-[12px] font-bold text-[var(--ink)]">{s.title}</div><div className="text-[11.5px] text-[var(--gray2)]">{s.why}</div></div>
                              <button onClick={() => onOpenClaim(g.patients.map((x) => x.rep.n), g.patients.findIndex((x) => x.key === p.key))} className="text-[10.5px] font-semibold text-[#4B4BBF] px-2 py-1 whitespace-nowrap flex-shrink-0">Open to apply →</button>
                            </div>
                          )) : <div className="text-[11.5px] text-[var(--gray2)]">No specific suggestions — open the claim form to review.</div>}
                          <div className="flex items-center gap-2 mt-3 pt-2 border-t border-black/[.06]">
                            <button onClick={() => setExpand(null)} className="text-[11.5px] font-semibold text-[var(--gray2)]">Close</button>
                            <span className="flex-1" />
                            <button onClick={() => onOpenClaim(g.patients.map((x) => x.rep.n), g.patients.findIndex((x) => x.key === p.key))} data-testid={`ny-openws-${p.key}`} className="text-[11.5px] font-bold text-white bg-[var(--ink)] rounded-[9px] px-3 py-1.5">Open claim form to apply →</button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {hiddenClaims > 0 ? (
                  <button onClick={() => navigate("/create-claims")} data-testid={`ny-more-${g.key}`} className="w-full text-left py-2.5 text-[11.5px] font-semibold hover:underline flex items-center gap-2" style={{ color: "#5856D6" }}>
                    See all {bandClaims} in the queue → <em className="not-italic text-[#9B9B97]">{hiddenClaims} more worth {money(hiddenVal)}</em>
                  </button>
                ) : null}
              </>
            ) : null}
          </div>
        );
      })}
      </>
      )}
    </div>
  );
}

// ---- Today's uploads: the real extraction stages, honestly ----
// The EOB pipeline exposes only status (processing / processed / failed) — no live percentage,
// claim-fraction or recovery-round field exists on the file/job records (verified against the API),
// so a running EOB row shows an INDETERMINATE bar and no numbers. Note-to-claim exposes stage +
// stage_message (a real per-step sentence) but no percentage. Terminal states are fully real:
// Complete → counts + Open, Failed → reason + Deep recovery (POST /eobs/files/{id}/deep-recovery).
const STAGE_NICE = { extracting: "Extracting claims", ocr: "Reading the document", validating: "Validating", icd_validate: "Coding", payer: "Matching payer", combinations: "Coding", visit: "Reading", coding: "Coding", finalizing: "Finalizing", billing_output: "Finalizing" };
const PROG_RANK = { processing: 0, ready: 1, failed: 2 };

function TodaysUploads({ notes, eobFiles, docs, navigate, notesReload, eobsReload, pendingJobs, onOpenClaim, onOpenCheck }) {
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(null);
  const PAGE = 8;

  const all = useMemo(() => {
    const list = buildUploads(notes, eobFiles, docs, true);
    const have = new Set(list.map((u) => String(u.id)));
    const pend = (pendingJobs || [])
      .filter((j) => !have.has(String(j.id)))
      .map((j) => ({
        kind: "note", id: j.id, fileName: j.filename, title: j.filename || "Your document", typeLabel: "Claim document",
        progress: "processing", stage: "queued", time: new Date().toISOString(),
        raw: { stage_message: "Coding it now." }, cptCount: 0, icdCount: 0,
      }));
    return [...pend, ...list].sort((a, b) => (PROG_RANK[a.progress] - PROG_RANK[b.progress]) || String(b.time || "").localeCompare(String(a.time || "")));
  }, [notes, eobFiles, docs, pendingJobs]);

  const totalPages = Math.max(1, Math.ceil(all.length / PAGE));
  const pg = Math.min(page, totalPages - 1);
  const pageItems = all.slice(pg * PAGE, pg * PAGE + PAGE);

  const running = all.filter((u) => u.progress === "processing").length;
  const ready = all.filter((u) => u.progress === "ready").length;
  const failed = all.filter((u) => u.progress === "failed").length;
  const meta = `${all.length} today · ${running} still running · ${ready} ready · ${failed} failed`;

  const stageName = (u) => u.progress === "failed" ? "Failed" : u.progress === "ready" ? "Complete"
    : u.kind === "note" ? (STAGE_NICE[String(u.stage || "").toLowerCase()] || "Processing") : "Extracting claims";
  const stageDot = (u) => u.progress === "failed" ? "#C4302A" : u.progress === "processing" ? "#5856D6" : "#C7C7CC";

  const stepSentence = (u) => {
    if (u.progress === "failed") return u.kind === "eob"
      ? "The first pass could not read this document — a heavier recovery pass is available."
      : (cleanEobErr(u.error) || "Coding didn't finish — retry to run it again.");
    if (u.progress === "ready") return u.kind === "eob"
      ? `${u.claims != null ? u.claims : "—"} claim${u.claims === 1 ? "" : "s"} read${u.raw?.filename ? "" : ""}`
      : u.kind === "note" ? `${u.cptCount || 0} CPT · ${u.icdCount || 0} dx coded` : (u.extracted ? `Read ${u.extracted}` : "Read and filed");
    // running
    if (u.kind === "note") return u.raw?.stage_message || "Coding the note.";
    return "Reading the remittance and extracting its claims.";
  };

  const deepRecover = async (u) => {
    setBusy(u.id);
    try { await runDeepRecovery(u.id); toast.success("Deep recovery started"); await eobsReload?.(); }
    catch (e) { toast.error(describeError(e)); } finally { setBusy(null); }
  };
  const retryNote = async (u) => {
    setBusy(u.id);
    try { await api.post(`/api/edi/note-to-claim/notes/${u.id}/reprocess`, {}); await notesReload?.(); }
    catch (e) { toast.error(describeError(e)); } finally { setBusy(null); }
  };

  return (
    <div className="glass-surface rounded-[20px] p-5" data-testid="home-uploads">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="text-[14px] font-bold text-[var(--ink)]">Today's uploads</div>
        <span className="text-[11px] font-medium text-[var(--gray2)]" data-testid="uploads-meta">{meta}</span>
        <span className="flex-1" />
        <button onClick={() => navigate("/create-claims")} className="text-[11.5px] font-semibold text-[#5856D6] hover:underline" data-testid="home-all-claims">All in Create Claims →</button>
      </div>

      {all.length === 0 ? (
        <div className="text-[12px] text-[var(--gray2)] py-4 text-center" data-testid="uploads-empty">No uploads today — drop a note, superbill, or EOB in Create Claims.</div>
      ) : (
        <>
          {pageItems.map((u) => {
            const isRun = u.progress === "processing", isReady = u.progress === "ready", isFail = u.progress === "failed";
            const kb = u.raw?.file_size ? `${Math.round(u.raw.file_size / 1024).toLocaleString()} KB` : null;
            return (
              <div key={`${u.kind}-${u.id}`} title={u.fileName || undefined} className="flex items-start gap-4 px-1 py-3.5 border-b border-[#F1F1F4] last:border-b-0" data-testid={`home-upload-${u.id}`}>
                {/* file identity */}
                <div className="w-[190px] flex-shrink-0 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[var(--ink)] truncate">{u.fileName || u.title}</div>
                  <div className="text-[10.5px] text-[#9B9B97] mt-0.5">{kb || u.typeLabel}</div>
                </div>
                {/* stage + step sentence */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 text-[12.5px] font-semibold" style={{ color: isFail ? "#C4302A" : "var(--ink)" }}>
                    <span className="w-[7px] h-[7px] rounded-full flex-shrink-0" style={{ background: stageDot(u) }} />
                    {stageName(u)}
                  </div>
                  <div className="text-[11px] text-[var(--gray2)] mt-1 leading-snug pr-3">{stepSentence(u)}</div>
                </div>
                {/* progress bar */}
                <div className="hidden md:block w-[150px] flex-shrink-0 pt-1">
                  <div className="relative h-[6px] rounded-full overflow-hidden" style={{ background: "#EEEEF1" }} data-testid={`upload-bar-${u.id}`}>
                    {isRun ? <span className="revq-bar-sweep" />
                      : isReady ? <span className="absolute inset-0 rounded-full" style={{ background: "#141412" }} />
                      : <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: "22%", background: "#E0574F" }} />}
                  </div>
                </div>
                {/* action — running rows offer none */}
                <div className="w-[112px] flex-shrink-0 flex justify-end">
                  {isRun ? (
                    <span className="text-[10.5px] text-[#B8B8BD]" data-testid={`upload-wait-${u.id}`}>nothing to do yet</span>
                  ) : isFail && u.kind === "eob" ? (
                    <button onClick={() => deepRecover(u)} disabled={busy === u.id} data-testid={`upload-recover-${u.id}`}
                      className="text-[11px] font-semibold text-[#C8372D] bg-white border border-[#E0B4AE] rounded-[9px] px-2.5 py-1.5 hover:bg-[#FDECEA] transition disabled:opacity-50">
                      {busy === u.id ? "…" : "Deep recovery"}
                    </button>
                  ) : isFail ? (
                    <button onClick={() => retryNote(u)} disabled={busy === u.id} data-testid={`upload-retry-${u.id}`}
                      className="text-[11px] font-semibold text-[#C8372D] bg-white border border-[#E0B4AE] rounded-[9px] px-2.5 py-1.5 hover:bg-[#FDECEA] transition disabled:opacity-50">
                      {busy === u.id ? "…" : "Retry"}
                    </button>
                  ) : (
                    <button onClick={() => { if (u.kind === "eob") { onOpenCheck ? onOpenCheck(u.raw) : navigate("/payments"); } else if (u.kind === "doc") { navigate("/patients"); } else { onOpenClaim ? onOpenClaim([u.raw], 0) : navigate("/create-claims"); } }} data-testid={`upload-open-${u.id}`}
                      className="text-[11px] font-semibold text-[var(--ink)] bg-white border border-black/[.12] rounded-[9px] px-3 py-1.5 hover:bg-black/[.04] transition">Open</button>
                  )}
                </div>
              </div>
            );
          })}
          {all.length > PAGE ? (
            <div className="flex items-center gap-3 mt-3 pt-2 border-t border-black/[.06] text-[11px] text-[var(--gray2)]">
              <span data-testid="uploads-pager">Showing {pg * PAGE + 1}–{Math.min(pg * PAGE + PAGE, all.length)} of {all.length}</span>
              <span className="flex-1" />
              <button disabled={pg === 0} onClick={() => setPage(pg - 1)} data-testid="uploads-prev" className="px-2 py-1 rounded-md border border-black/[.1] disabled:opacity-40">‹</button>
              <span>Page {pg + 1} of {totalPages}</span>
              <button disabled={pg >= totalPages - 1} onClick={() => setPage(pg + 1)} data-testid="uploads-next" className="px-2 py-1 rounded-md border border-black/[.1] disabled:opacity-40">›</button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ---- Agent activity: single honest Coding-engine timeline from note timestamps ----
const AG_HOURS = [6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17];
const hourLabel = (h) => (h === 12 ? "12p" : h > 12 ? `${h - 12}p` : `${h}a`);

function AgentActivity() {
  const { selectedClientId } = useClient();
  const [note, setNote] = useState(null);
  const [eob, setEob] = useState(null);
  const [lens, setLens] = useState({ ready: 0, running: false, inflight: 0, queued: 0, workers: 2, cap: 2, avgMs: null, inflightItems: [] });
  const [now, setNow] = useState(Date.now());
  const [paused, setPaused] = useState(false);

  // Documents don't show in queue-health; union in the split-batches read (Lumina Lens pipeline) for the active client.
  useEffect(() => {
    if (!selectedClientId) { setLens((p) => ({ ...p, ready: 0, running: false, inflight: 0, queued: 0, inflightItems: [] })); return undefined; }
    let alive = true, t;
    const tick = async () => {
      const L = { ready: 0, running: false, inflight: 0, queued: 0, workers: 2, cap: 2, avgMs: null, inflightItems: [] };
      try {
        const bs = await listSplitBatches({ client_id: selectedClientId });
        for (const b of bs) {
          const segs = b.segments || [];
          L.ready += b.num_ingested ?? segs.filter((s) => s.status === "ingested").length;
          const proc = segs.filter((s) => s.status === "processing").length;
          const q = segs.filter((s) => s.status === "queued").length;
          L.inflight += proc; L.queued += q;
          if (b.status === "processing" || proc || q) { L.running = true; L.inflightItems.push({ filename: b.filename }); }
        }
      } catch { return; }
      if (!alive) return;
      setLens(L);
      t = setTimeout(tick, L.running ? 8000 : 30000);
    };
    tick();
    const kick = () => { clearTimeout(t); tick(); };
    window.addEventListener("revq:split-started", kick); window.addEventListener("focus", kick);
    return () => { alive = false; clearTimeout(t); window.removeEventListener("revq:split-started", kick); window.removeEventListener("focus", kick); };
  }, [selectedClientId]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      getNoteQueueHealth().then((d) => alive && setNote(d)).catch(() => {});
      getEobQueueHealth().then((d) => alive && setEob(d)).catch(() => {});
      if (alive) setNow(Date.now());
    };
    load();
    const t = setInterval(load, 60000); // nobody's watching — slow poll, two pipelines only
    return () => { alive = false; clearInterval(t); };
  }, []);

  const stat = (qh) => {
    const j = qh?.jobs || {}; const c = j.counts || {};
    const recent = j.recent_finished || [];
    const durs = recent.map((r) => r.stage_durations_ms?.total).filter((x) => x > 0);
    const inflight = (j.by_tenant_inflight || []).reduce((s, t) => s + (t.count ?? t.in_flight ?? 1), 0);
    return {
      ready: c.ready || 0, failed: c.failed || 0, queued: c.queued || 0,
      workers: j.worker_count ?? 2, cap: j.per_tenant_cap ?? 2, revivals: j.revivals_count || 0,
      inflight, running: inflight > 0 || (c.processing || 0) > 0,
      avgMs: durs.length ? Math.round(durs.reduce((a, b) => a + b, 0) / durs.length) : null,
      reprocessed: recent.filter((r) => (r.attempts || 1) > 1).length,
      inflightItems: j.by_tenant_inflight || [],
    };
  };

  if (!note && !eob) return <div className="glass-surface rounded-[20px] p-5 text-[12px] text-[var(--gray2)]" style={{ minHeight: 430 }} data-testid="home-agents">Loading worker pools…</div>;
  const n = stat(note), e = stat(eob);
  const passes = n.ready + e.ready;
  const failed = n.failed + e.failed;
  const cleanPct = passes + failed > 0 ? (passes / (passes + failed)) * 100 : 100;
  const avgMs = [n.avgMs, e.avgMs].filter(Boolean).length ? Math.round(([n.avgMs || 0, e.avgMs || 0].reduce((a, b) => a + b, 0)) / [n.avgMs, e.avgMs].filter(Boolean).length) : null;
  const stranded = n.revivals + e.revivals;
  const reprocessed = n.reprocessed + e.reprocessed;
  const inflightTotal = n.inflight + e.inflight;
  const queuedTotal = n.queued + e.queued;
  const working = n.running || e.running || lens.running;

  const timeAgo = (iso) => { if (!iso) return ""; const s = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000)); if (s < 60) return `${s}s ago`; const m = Math.floor(s / 60); if (m < 60) return `${m}m ${s % 60}s ago`; const h = Math.floor(m / 60); if (h < 24) return `${h}h ${m % 60}m ago`; return `${Math.floor(h / 24)}d ago`; };
  const shortErr = (le) => { if (!le) return "Failed"; const t = typeof le === "string" ? le : JSON.stringify(le); if (/TOKEN_LIMIT|Free.?Tier|429/i.test(t)) return "Free-tier token limit reached (429)"; const m = t.match(/"message"\s*:\s*"([^"\n]{0,80})/); return m ? m[1] : t.slice(0, 80); };
  const isInfra = (le) => /TOKEN_LIMIT|Free.?Tier|429|rate.?limit/i.test(typeof le === "string" ? le : JSON.stringify(le || ""));

  const ticker = [
    ...(note?.jobs?.recent_finished || []).map((r) => ({ ...r, pipe: "Coding engine" })),
    ...(eob?.jobs?.recent_finished || []).map((r) => ({ ...r, pipe: "EOB extraction" })),
  ].sort((a, b) => new Date(b.finished_at_iso || 0) - new Date(a.finished_at_iso || 0)).slice(0, 12);

  const agents = [
    { key: "note", label: "Coding engine", sub: "note → claim", s: n },
    { key: "eob", label: "EOB extraction", sub: "remit → 835", s: e },
    { key: "lens", label: "Lumina Lens", sub: "scan → documents", s: lens },
  ];
  const hstat = (k, v, s, drv) => (
    <div className="flex-1 min-w-[120px]"><div className="text-[10.5px] font-semibold text-[var(--gray2)] flex items-center gap-1">{k}{drv ? <span className="text-[9px] text-[#B8B8BD] border border-black/[.1] rounded px-1">derived</span> : null}</div><div className="text-[19px] font-extrabold text-[var(--ink)] tabular-nums mt-0.5"><SoftValue>{v}</SoftValue></div><div className="text-[10px] text-[#9B9B97]">{s}</div></div>
  );

  return (
    <div className="glass-surface rounded-[20px] p-5" style={{ minHeight: 430 }} data-testid="home-agents">
      <div className="flex items-center gap-2.5 mb-4 flex-wrap">
        <div className="text-[14px] font-bold text-[var(--ink)]">Agent activity</div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2 py-[2px]" style={{ background: working ? "#FDF4E5" : "#E7F6EE", color: working ? "#9A5A00" : "#1f7a46" }} data-testid="agents-status">
          <span className="w-[6px] h-[6px] rounded-full" style={{ background: working ? "#FF9500" : "#22C55E" }} />{working ? "Working" : "Idle"}
        </span>
        <span className="text-[11px] font-medium text-[var(--gray2)]">worker pools</span>
        <span className="text-[10.5px] font-semibold rounded-full px-2 py-[2px] bg-black/[.05] text-[var(--gray2)]" data-testid="agents-scope">across all clients in this tenant</span>
      </div>

      {/* hero */}
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4 pb-4 mb-4 border-b border-black/[.06]" data-testid="agents-hero">
        <div className="flex items-baseline gap-3">
          <div className="text-[40px] font-extrabold text-[var(--ink)] tabular-nums leading-none"><SoftValue>{passes}</SoftValue></div>
          <div className="text-[12px] text-[var(--gray2)] max-w-[190px]">successful passes, <b className="text-[var(--ink)]">without anyone asking</b><span className="block text-[10.5px] text-[#9B9B97] mt-0.5">{reprocessed > 0 ? `${reprocessed} were a 2nd pass (recent)` : "no retries in recent activity"}</span></div>
        </div>
        <div className="flex flex-1 gap-6 flex-wrap">
          {hstat("Clean first time", `${cleanPct.toFixed(1)}%`, failed ? `${failed} needed a retry` : "no failures", true)}
          {hstat("Average per document", avgMs ? `${(avgMs / 1000).toFixed(0)}s` : "—", "from the per-stage timings", false)}
          {hstat("Stranded", stranded ? String(stranded) : "none", "nothing revived in recent activity", false)}
        </div>
      </div>

      {/* pipeline rows */}
      <div className="space-y-2.5" data-testid="agents-pipes">
        {agents.map((a) => (
          <div key={a.key} className="flex items-center gap-3" data-testid={`agents-pipe-${a.key}`}>
            <div className="w-[150px] flex-shrink-0"><div className="text-[12.5px] font-bold text-[var(--ink)] flex items-center gap-1.5"><span className="w-[7px] h-[7px] rounded-full" style={{ background: a.s.running ? "#5856D6" : "#141412" }} />{a.label}</div><div className="text-[10.5px] text-[var(--gray2)] ml-3">{a.sub}</div></div>
            <div className="flex-1 min-w-0">
              <div className="h-[8px] rounded-full overflow-hidden" style={{ background: "rgba(0,0,0,.06)" }}>
                {a.s.running ? <div className="h-full w-1/3 rounded-full animate-pulse" style={{ background: "#5856D6" }} /> : <div className="h-full rounded-full" style={{ background: "#141412", width: "100%", opacity: 0.15 }} />}
              </div>
              <div className="flex items-center gap-2 mt-1 text-[10.5px] text-[var(--gray2)]">
                {a.s.running ? <b style={{ color: "#4B4BBF" }}>{a.s.inflight} in flight</b> : <span>idle</span>}<span>· {a.s.ready} done so far</span><span className="flex-1" />{a.s.avgMs ? <span>{(a.s.avgMs / 1000).toFixed(1)}s average</span> : null}
              </div>
            </div>
            <div className="text-right w-[70px] flex-shrink-0"><div className="text-[15px] font-extrabold text-[var(--ink)] tabular-nums">{a.s.ready}</div><div className="text-[9.5px] text-[#9B9B97]">documents</div></div>
            <div className="text-[10px] text-[var(--gray2)] w-[100px] flex-shrink-0 leading-tight"><b>{a.s.workers}</b> workers · cap {a.s.cap}{a.s.running ? <><br /><b style={{ color: "#4B4BBF" }}>{a.s.inflight} in flight</b></> : null}</div>
          </div>
        ))}
      </div>

      {/* running now / idle */}
      {inflightTotal > 0 || queuedTotal > 0 ? (
        <div className="mt-4 rounded-[12px] bg-black/[.02] p-3" data-testid="agents-flight">
          <div className="text-[11px] font-bold text-[var(--ink)] mb-1">Running now<span className="text-[10px] font-normal text-[#9B9B97] ml-2">the payload exposes no percentage — a count, not a progress bar</span></div>
          {agents.flatMap((a) => (a.s.inflightItems || []).filter((it) => it.filename).map((it, i) => (
            <div key={`${a.key}-${i}`} className="flex items-center gap-2 text-[11.5px] py-1"><span className="w-[6px] h-[6px] rounded-full animate-pulse" style={{ background: "#5856D6" }} /><span className="truncate max-w-[260px]">{it.filename}</span><span className="text-[#9B9B97]">{a.label}</span></div>
          )))}
          {inflightTotal > 0 && !agents.some((a) => (a.s.inflightItems || []).some((it) => it.filename)) ? <div className="text-[11.5px] text-[var(--gray2)]"><b style={{ color: "#4B4BBF" }}>{inflightTotal}</b> running now across pipelines</div> : null}
          {queuedTotal > 0 ? <div className="text-[11px] text-[var(--gray2)] mt-1"><b>{queuedTotal}</b> waiting for a worker slot · per-tenant cap {n.cap}, so revQ runs {n.cap} at a time and queues the rest</div> : null}
        </div>
      ) : null}

      {/* just finished ticker */}
      <div className="mt-4" data-testid="agents-ticker">
        <style>{`@keyframes rqMarquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
        <div className="flex items-center mb-1.5">
          <span className="text-[11px] font-bold text-[var(--ink)]">Just finished</span>
          <span className="text-[10px] font-normal text-[#9B9B97] ml-2">live tape — the last 20 the API returns · hover to pause</span>
        </div>
        {ticker.length === 0 ? <div className="text-[11px] text-[var(--gray2)]">Nothing finished recently.</div> : (
          <div className="relative overflow-hidden rounded-[10px] border border-black/[.06] bg-black/[.015] py-2"
            onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
            style={{ maskImage: "linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent)", WebkitMaskImage: "linear-gradient(90deg,transparent,#000 5%,#000 95%,transparent)" }}>
            <div className="flex w-max gap-2.5" style={{ animation: `rqMarquee ${Math.max(24, ticker.length * 4)}s linear infinite`, animationPlayState: paused ? "paused" : "running" }}>
              {[...ticker, ...ticker].map((r, i) => {
                const bad = r.status === "failed" || r.status === "error" || r.last_error;
                const infra = bad && isInfra(r.last_error);
                return (
                  <div key={i} className="flex items-center gap-2 text-[11.5px] whitespace-nowrap flex-shrink-0 rounded-full border border-black/[.08] bg-white px-3 py-1.5" data-testid={i < ticker.length ? `agents-tick-${i}` : undefined}>
                    <span className="w-[6px] h-[6px] rounded-full flex-shrink-0" style={{ background: bad ? "#C4302A" : "#22C55E" }} />
                    <span className="font-semibold text-[var(--ink)] max-w-[220px] truncate">{r.filename}</span>
                    <span className="text-[#9B9B97]">{r.pipe}</span>
                    {(r.attempts || 1) > 1 && !bad ? <span className="text-[9.5px] font-semibold rounded px-1 py-[1px]" style={{ background: "#EEF0FF", color: "#4B4BBF" }}>2nd pass</span> : null}
                    {bad ? <span className="text-[10.5px] font-semibold" style={{ color: "#C4302A" }}>{shortErr(r.last_error)}</span> : null}
                    {infra ? <span className="text-[10px] text-[#9A5A00]">retries on its own</span> : null}
                    <span className="text-[10.5px] text-[#9B9B97]">· {timeAgo(r.finished_at_iso)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-black/[.06] text-[10.5px] text-[#9B9B97]" data-testid="agents-foot">
        <span><b>{n.workers + e.workers}</b> workers · per-tenant cap {n.cap} · two pipelines polled every 60s · refreshed {new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
      </div>
    </div>
  );
}

/* LiveActivityPulse moved to components/LiveActivityPulse.jsx */
function TodayTab({ enriched, navigate, notes, eobFiles, docs, notesReload, eobsReload, onCount, pendingJobs }) {
  const [insNote, setInsNote] = useState(null);
  const [claimQ, setClaimQ] = useState(null); // { items, index }
  const [checkFile, setCheckFile] = useState(null); // EOB file for L1 workspace
  return (
    <>
      {/* needs you today — ranked brief across both money directions */}
      <NeedsYouBrief enriched={enriched} navigate={navigate} eobFiles={eobFiles} eobsReload={eobsReload}
        onCount={onCount} onOpenClaim={(items, index) => setClaimQ({ items, index })} />

      {/* today's uploads — merged notes + EOB/ERA */}
      <TodaysUploads notes={notes} eobFiles={eobFiles} docs={docs} navigate={navigate} notesReload={notesReload} eobsReload={eobsReload} pendingJobs={pendingJobs}
        onOpenClaim={(items, index) => setClaimQ({ items, index })} onOpenCheck={(f) => setCheckFile(f)} />

      {insNote ? <InsuranceModal note={insNote} onClose={() => setInsNote(null)} onUpdated={notesReload} /> : null}
      {claimQ ? (
        <ClaimWorkspace items={claimQ.items} index={claimQ.index}
          onIndex={(i) => setClaimQ((q) => ({ ...q, index: i }))}
          backLabel="Needs you today" onClose={() => setClaimQ(null)} onChanged={notesReload} />
      ) : null}
      {checkFile ? (
        <CheckWorkspace file={checkFile} navigate={navigate}
          onClose={(changed) => { setCheckFile(null); if (changed) eobsReload?.(); }} />
      ) : null}
    </>
  );
}
